import { useState, useEffect } from "react";
import { SEED_CRISIS_DATA } from "./dev-fixtures";
import SignalJournal from "./SignalJournalView";
import { triggerLabel, actionLabel } from "./signalJournal";
import { normalizeOptions } from "./formOptions";

// ⚠ Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to Vercel environment variables.
// This project uses Create React App (react-scripts) which requires the REACT_APP_ prefix.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://aedhhhnsfspodmbxcedy.supabase.co";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlZGhoaG5zZnNwb2RtYnhjZWR5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE4NDQxMzcsImV4cCI6MjA4NzQyMDEzN30.7izgMmHeGYd4-aPMjocvNz8ubBXHgWZVlbmksi9QqYY";

// ── AUTH HELPERS ──────────────────────────────────────────────────────────────
const AUTH_STORAGE_KEY = "wip_session";

function loadSession() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s.expiresAt && Date.now() > s.expiresAt) { localStorage.removeItem(AUTH_STORAGE_KEY); return null; }
    return s;
  } catch { return null; }
}
function saveSession(s) {
  try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ ...s, expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 })); } catch {}
}
function clearSession() {
  try { localStorage.removeItem(AUTH_STORAGE_KEY); } catch {}
}

async function authSendOTP(phone) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/otp`, {
    method: "POST",
    headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ phone }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.msg || e.error_description || "Could not send code. Check your number and try again.");
  }
  return true;
}

async function authVerifyOTP(phone, token) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/verify`, {
    method: "POST",
    headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ type: "sms", phone, token }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.msg || e.error_description || "Invalid code. Please try again.");
  }
  return res.json();
}

async function fetchLinkedWarriorId(accessToken, userId) {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/warriors?id=eq.${userId}&select=warrior_id`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const rows = await res.json();
    return rows.length && rows[0].warrior_id ? rows[0].warrior_id : null;
  } catch { return null; }
}

async function saveWarriorLink(accessToken, userId, warriorId, phone) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/warriors`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Prefer: "resolution=merge-duplicates",
    },
    body: JSON.stringify({ id: userId, warrior_id: warriorId, phone_number: phone }),
  });
  return res.ok || res.status === 201 || res.status === 200;
}

// Shared row shape for both the personal (public_crisis_data) and community
// (community_crisis_patterns) endpoints. The community view omits warrior_id
// and tracking_for entirely, so those simply fall back to "" for those rows.
function mapCrisisRow(r) {
  return {
    id: r.submission_id,
    warriorId: r.warrior_id || "",
    submitted: r.submitted_at ? r.submitted_at.slice(0,10) : "",
    crisisStart: r.crisis_start || "",
    ongoing: r.ongoing === true || r.ongoing === "Yes, still ongoing",
    pain: Number(r.pain_level) || 0,
    painCompared: r.pain_compared || "",
    painLocations: normalizeOptions("painLocations", r.pain_locations ? r.pain_locations.split(" | ") : []),
    triggered: normalizeOptions("triggered", r.triggered_by ? r.triggered_by.split(" | ") : []),
    primaryTriggers: normalizeOptions("triggered", r.primary_triggers ? r.primary_triggers.split(" | ") : []),
    predictability: r.predictability || "",
    temp: r.temperature ? parseFloat(r.temperature) : null,
    erVisit: r.er_visit || "",
    hospital: r.hospital || "",
    waitHours: r.wait_hours != null ? r.wait_hours : 0,
    triageToTreatment: r.triage_to_treatment_hours ? parseFloat(r.triage_to_treatment_hours) : null,
    protocolFollowed: r.protocol_followed || "",
    whyNot: r.why_not_followed || "",
    treatment: normalizeOptions("treatment", r.treatment ? r.treatment.split(" | ") : []),
    admitted: r.admitted || "",
    outcome72h: r.outcome_72h || null,
    working: r.treatment_working || "",
    trackingFor: r.tracking_for || "",
    community: r.community || "",
    age: r.age ? Number(r.age) : null,
    consented: r.consent === true || r.consent === "true",
    notes: r.notes || "",
    hydrationOz: r.hydration_oz ? Number(r.hydration_oz) : null,
    hrv: r.hrv_mssd ? parseFloat(r.hrv_mssd) : null,
    skinTempDelta: r.skin_temp_delta ? parseFloat(r.skin_temp_delta) : null,
    sleepEfficiency: r.sleep_efficiency ? parseFloat(r.sleep_efficiency) : null,
  };
}

// Personal timeline. Must use the signed-in Warrior's own access token, never
// the anon key: RLS (authenticated_read_own, case-insensitive) is the actual
// boundary, so no client-side warrior_id filter is applied here.
async function fetchWarriorData(accessToken) {
  if (!accessToken) return [];
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/public_crisis_data?select=*&order=submitted_at.asc`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  );
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    console.error("fetchWarriorData failed", res.status, errBody);
    throw new Error("Failed to fetch");
  }
  const rows = await res.json();
  return rows.map(mapCrisisRow);
}

// Anonymized, consent-filtered, cross-Warrior data for Community Patterns /
// ER Accountability. Backed by the community_crisis_patterns view (no
// warrior_id/tracking_for), readable by signed-in Warriors only — signed-out
// visitors and fetch failures keep the demo dataset.
async function fetchCommunityData(accessToken) {
  if (!accessToken) return [];
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/community_crisis_patterns?select=*&order=submitted_at.asc`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
  );
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    console.error("fetchCommunityData failed", res.status, errBody);
    return [];
  }
  const rows = await res.json();
  return rows.map(mapCrisisRow);
}

// Synthetic demo records only (see dev-fixtures.js). Real Warrior data is
// never bundled into the client — it is fetched per-Warrior under RLS.
const RAW = [...SEED_CRISIS_DATA];
// Snapshot of the demo data — used as fallback when Supabase returns nothing
const RAW_BACKUP = RAW.slice();

// Cross-Warrior data for Community Patterns / ER Accountability / Intelligence,
// kept separate from RAW (which holds only the signed-in Warrior's own records).
// Starts as the demo data; replaced once fetchCommunityData succeeds.
const COMMUNITY_RAW = [...RAW];

// ── FUZZY MATCH ───────────────────────────────────────────────────────────────
function norm(s){return s.toLowerCase().replace(/[\s\-_.$]/g,"").trim();}
function levenshtein(a,b){
  const dp=Array.from({length:a.length+1},(_,i)=>Array.from({length:b.length+1},(_,j)=>j===0?i:0));
  for(let j=1;j<=b.length;j++)dp[0][j]=j;
  for(let i=1;i<=a.length;i++)for(let j=1;j<=b.length;j++)
    dp[i][j]=a[i-1]===b[j-1]?dp[i-1][j-1]:1+Math.min(dp[i-1][j],dp[i][j-1],dp[i-1][j-1]);
  return dp[a.length][b.length];
}
function findMatches(query){
  const q=norm(query);
  if(!q)return{exact:[],fuzzy:[]};
  const byKey={};
  RAW.forEach(d=>{const k=norm(d.warriorId);if(!byKey[k])byKey[k]={warriorId:d.warriorId,entries:[]};byKey[k].entries.push(d);});
  const exact=[],fuzzy=[];
  Object.entries(byKey).forEach(([k,val])=>{
    if(k===q||k.startsWith(q)||q.startsWith(k.slice(0,Math.min(6,k.length))))exact.push(val);
    else{const dist=levenshtein(q,k);if(dist<=Math.max(2,Math.floor(Math.max(q.length,k.length)*0.28)))fuzzy.push({...val,dist});}
  });
  return{exact,fuzzy:fuzzy.sort((a,b)=>a.dist-b.dist).slice(0,3)};
}

// ── HELPERS ───────────────────────────────────────────────────────────────────
const TS={"Cold weather exposure":"THERMAL SHOCK","High stress situation":"High Stress","Lack of sleep":"Lack of Sleep","Dehydration (missed water intake)":"Dehydration","Menstrual cycle":"Menstrual Cycle","Illness (cold, flu, infection)":"Illness/Infection","Physical overexertion":"Overexertion","Changed altitude/air pressure":"Altitude/Pressure","Skipped medication":"Skipped Medication","Temperature or weather change":"Weather Change","Sex":"Sexual Activity"};
const avgPain=e=>{const v=e.map(x=>x.pain).filter(p=>p>0);return v.length?(v.reduce((a,b)=>a+b,0)/v.length).toFixed(1):"—";};
const fmtWait=w=>{
  if(!w||w===0||w==="0")return null;
  if(typeof w==="string"){
    if(w.includes("min"))return w;
    if(w.includes("hour"))return w;
    const n=parseFloat(w);
    if(!isNaN(n)&&n>0){if(n<1)return`${Math.round(n*60)} min wait`;return`${n}h wait`;}
    return w; // return as-is for strings like "31-60 mins"
  }
  if(typeof w==="number"&&w>0){if(w<1)return`${Math.round(w*60)} min wait`;return`${w}h wait`;}
  return null;
}; 
const normPC=s=>{if(!s)return s;return s.replace(/Worse thn usual/i,"Worse than usual").replace(/Better thn usual/i,"Better than usual");}; 
const normAdmitted=s=>{if(!s||s==="N/A"||s==="n/a")return null;const l=s.toLowerCase();if(l.includes("still in hospital"))return"Still Hospitalized";if(l.includes("yes, i was admitted")||s==="Yes")return"Admitted";if(l.includes("no, i was treated")||s==="No")return"Treated & Released";return s;};
const countT=entries=>{const c={};entries.forEach(e=>e.triggered.forEach(t=>{const s=TS[t]||t;c[s]=(c[s]||0)+1;}));return Object.entries(c).sort((a,b)=>b[1]-a[1]);};
const days=(a,b)=>Math.round((new Date(b)-new Date(a))/(864e5));
const allViolations=()=>COMMUNITY_RAW.filter(d=>d.erVisit==="Yes"&&(d.protocolFollowed==="No"||(d.whyNot&&d.whyNot.toLowerCase().match(/medical team|care team/))));
const allReturning=()=>{const m={};COMMUNITY_RAW.forEach(d=>{const k=norm(d.warriorId);if(!m[k])m[k]=[];m[k].push(d);});return Object.values(m).filter(a=>a.length>1).sort((a,b)=>b.length-a.length);};

// ── TOKENS ────────────────────────────────────────────────────────────────────
const C={bg:"#0c1524",surf:"#152036",surf2:"#1a2a42",border:"rgba(255,255,255,0.08)",muted:"rgba(255,255,255,0.42)",text:"rgba(255,255,255,0.92)",red:"#e85555",amber:"#F0B429",green:"#4ade80",blue:"#60a5fa",purple:"#c084fc",teal:"#2dd4bf"};
const painCol=p=>p>=8?C.red:p>=5?"#f0a500":C.green;
const css=`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=IBM+Plex+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:${C.bg};color:${C.text};font-family:'IBM Plex Sans',sans-serif;}
  input::placeholder{color:${C.muted};} input:focus{outline:none;}
  ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} .fade{animation:fadeUp .3s ease forwards;}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:0.4}} .live{animation:blink 2s infinite;}
  button{cursor:pointer;font-family:'IBM Plex Sans',sans-serif;}
`;

// ── HTML ESCAPE ───────────────────────────────────────────────────────────────
// Sanitizes any user-supplied or DB-sourced string before it enters dangerouslySetInnerHTML.
const esc=s=>String(s??'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');

// ── PRINT OVERLAY ────────────────────────────────────────────────────────────
// Global print state — avoids prop drilling
let _setPrintHTML = null;
function showPrintDoc(html){
  if(_setPrintHTML) _setPrintHTML(html);
}

function PrintOverlay(){
  const [html,setHTML]=useState(null);
  _setPrintHTML=setHTML;
  if(!html) return null;
  return(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"#fff",zIndex:300,overflowY:"auto"}}>
      <div style={{position:"fixed",top:0,left:0,right:0,background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"10px 16px",display:"flex",gap:10,alignItems:"center",zIndex:301,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <button onClick={()=>window.print()} style={{background:"#990000",color:"#fff",border:"none",borderRadius:8,padding:"9px 20px",fontSize:13,fontWeight:600,cursor:"pointer"}}>⬇ Save / Print</button>
        <button onClick={()=>setHTML(null)} style={{background:"none",border:"1px solid #d1d5db",borderRadius:8,padding:"9px 16px",fontSize:13,cursor:"pointer",color:"#374151"}}>✕ Close</button>
        <span style={{fontSize:11,color:"#888",marginLeft:4}}>Use your browser's share or print to save as PDF</span>
      </div>
      <div style={{marginTop:52}} dangerouslySetInnerHTML={{__html:html}}/>
    </div>
  );
}

// ── MICRO COMPONENTS ──────────────────────────────────────────────────────────
const Tag=({label,color})=>(<span style={{display:"inline-block",fontSize:11,padding:"2px 9px",borderRadius:20,background:`${color}18`,color,border:`1px solid ${color}28`,marginRight:4,marginBottom:4}}>{label}</span>);
const Pill=({label,value,accent,sub,tooltip})=>(<div style={{position:"relative",textAlign:"center",padding:"14px 16px",background:C.surf2,borderRadius:12,border:`1px solid ${C.border}`,borderTop:`2px solid ${accent}`}} title={tooltip||""}><div style={{fontSize:28,fontFamily:"Syne",fontWeight:800,color:accent,lineHeight:1}}>{value}</div><div style={{fontSize:11,color:C.muted,marginTop:3,letterSpacing:.4}}>{label}{tooltip&&<span style={{marginLeft:5,fontSize:10,opacity:.5,cursor:"help",verticalAlign:"middle"}}>ⓘ</span>}</div>{sub&&<div style={{fontSize:10,color:C.muted,marginTop:1}}>{sub}</div>}</div>);

// ── TIMELINE ENTRY ────────────────────────────────────────────────────────────
function Entry({e,isLast,onResolve,onLogViolation}){
  const [showLog,setShowLog]=useState(false);
  const [logForm,setLogForm]=useState({requested:"",response:""});
  const pc=painCol(e.pain);
  const viol=e.erVisit==="Yes"&&(e.protocolFollowed==="No"||(e.whyNot&&e.whyNot.toLowerCase().match(/medical team|care team/)));
  const dur=days(e.crisisStart,e.submitted);
  const isFirstConsented=e.consented&&e.id==="J9NPxyz";
  const isRecoveryArc=e.outcome72h&&e.outcome72h.includes("resolved");
  return(
    <div style={{display:"flex",gap:14}}>
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
        <div style={{width:13,height:13,borderRadius:"50%",background:pc,border:`2px solid ${C.bg}`,boxShadow:`0 0 10px ${pc}55`,marginTop:4,flexShrink:0}}/>
        {!isLast&&<div style={{width:2,flex:1,background:C.border,marginTop:3,minHeight:20}}/>}
      </div>
      <div style={{flex:1,paddingBottom:isLast?0:18}}>
        <div className="fade" style={{background:C.surf2,borderRadius:12,padding:"16px 18px",border:`1px solid ${C.border}`,borderLeft:`3px solid ${viol?C.red:isRecoveryArc?C.green:pc}`}}>
          {isFirstConsented&&<div style={{fontSize:10,letterSpacing:1.5,color:C.teal,textTransform:"uppercase",marginBottom:8,background:`${C.teal}12`,padding:"3px 10px",borderRadius:4,display:"inline-block"}}>✓ First Consented Submission — Mar 6, 2026</div>}
          {isRecoveryArc&&<div style={{fontSize:10,letterSpacing:1.5,color:C.green,textTransform:"uppercase",marginBottom:8,background:`${C.green}12`,padding:"3px 10px",borderRadius:4,display:"inline-block"}}>✓ Documented Recovery</div>}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:6}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:12,color:C.muted}}>{e.submitted}</span>
              {e.ongoing&&<span className="live" style={{fontSize:10,color:C.green,letterSpacing:.5}}>● ONGOING</span>}
              {e.consented&&<span style={{fontSize:10,color:C.teal}}>✓ Consented</span>}
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:11,fontFamily:"Syne",fontWeight:700,padding:"3px 10px",borderRadius:20,background:`${pc}18`,color:pc,border:`1px solid ${pc}30`}}>Pain {e.pain}/10</span>
              {e.painCompared!=="Same as usual"&&<span style={{fontSize:11,color:e.painCompared.includes("Worse")?C.red:C.green}}>{e.painCompared}</span>}
            </div>
          </div>
          <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Started {e.crisisStart}{dur>0&&<span style={{color:C.text}}> · {dur} day{dur!==1?"s":""} duration</span>}{e.age&&<span style={{color:C.muted}}> · Age {e.age}</span>}</div>
          {e.painLocations.length>0&&<div style={{marginBottom:8}}><span style={{fontSize:10,letterSpacing:1.5,color:C.muted,textTransform:"uppercase",marginRight:6}}>Where</span>{e.painLocations.map(l=><Tag key={l} label={l} color={C.blue}/>)}</div>}
          {e.triggered.length>0&&<div style={{marginBottom:8}}><span style={{fontSize:10,letterSpacing:1.5,color:C.muted,textTransform:"uppercase",marginRight:6}}>Triggers</span>{e.triggered.map(t=><Tag key={t} label={TS[t]||t} color={t.includes("Menstrual")?C.purple:C.amber}/>)}</div>}
          <div style={{fontSize:12,color:C.muted,marginBottom:e.erVisit==="Yes"||e.triageToTreatment||e.outcome72h||e.notes?8:0}}><span style={{fontSize:10,letterSpacing:1.5,textTransform:"uppercase",marginRight:6}}>Treatment</span><span style={{color:C.text}}>{e.treatment.join(", ")||"—"}</span>{e.working&&<span style={{color:C.muted}}> — {e.working}</span>}</div>
          {e.triageToTreatment&&<div style={{fontSize:12,marginBottom:8,color:C.muted}}><span style={{fontSize:10,letterSpacing:1.5,textTransform:"uppercase",marginRight:6}}>Triage→Treatment</span><span style={{color:C.text}}>{e.triageToTreatment}h</span></div>}
          {e.erVisit==="Yes"&&<div style={{marginTop:8,padding:"10px 14px",borderRadius:8,background:viol?`${C.red}0c`:`${C.green}0a`,border:`1px solid ${viol?C.red+"28":C.green+"25"}`}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:4,marginBottom:4}}><span style={{fontSize:13,fontWeight:500}}>🏥 {e.hospital||"ER Visit"}</span>{fmtWait(e.waitHours)&&<span style={{fontSize:11,color:C.muted}}>{fmtWait(e.waitHours)}</span>}</div><div style={{fontSize:12,color:viol?C.red:C.green}}>{viol?`✗ Protocol NOT followed${e.whyNot?` — ${e.whyNot}`:""}`:e.protocolFollowed==="Yes"?"✓ Protocol followed":( e.protocolFollowed==="No protocol" || e.protocolFollowed==="I don't have a protocol" || (e.protocolFollowed && e.protocolFollowed.toLowerCase().includes("don't have")) )?"⚠ No protocol on file":(!e.protocolFollowed?"⚠ Protocol not recorded":"Protocol status unclear")}</div>{(()=>{const _a=normAdmitted(e.admitted);return _a?<div style={{fontSize:11,color:C.muted,marginTop:3}}>Admission: {_a}</div>:null;})()}
          {e.protocolFollowed==="No"&&!e.whyNot&&(
            showLog?(
              <div style={{marginTop:10,padding:"12px 14px",borderRadius:8,background:`${C.amber}0a`,border:`1px solid ${C.amber}30`}}>
                <div style={{fontSize:11,color:C.amber,fontWeight:600,marginBottom:10,letterSpacing:.3}}>What happened at this visit?</div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <input placeholder="What you requested (e.g. IV Dilaudid per protocol)" value={logForm.requested} onChange={ev=>setLogForm(f=>({...f,requested:ev.target.value}))} style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 12px",color:C.text,fontSize:12,fontFamily:"'IBM Plex Sans',sans-serif",outline:"none"}}/>
                  <input placeholder="What the team did instead" value={logForm.response} onChange={ev=>setLogForm(f=>({...f,response:ev.target.value}))} style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 12px",color:C.text,fontSize:12,fontFamily:"'IBM Plex Sans',sans-serif",outline:"none"}}/>
                  <div style={{display:"flex",gap:8,marginTop:2}}>
                    <button onClick={()=>{const combined=[logForm.requested&&`Requested: ${logForm.requested}`,logForm.response&&`Response: ${logForm.response}`].filter(Boolean).join(" / ");if(combined&&onLogViolation)onLogViolation(e.id,combined);setShowLog(false);}} style={{background:C.amber,border:"none",borderRadius:6,padding:"7px 14px",color:"#000",fontSize:12,fontWeight:600,cursor:"pointer"}}>Save</button>
                    <button onClick={()=>setShowLog(false)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"7px 14px",color:C.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
                  </div>
                </div>
              </div>
            ):(
              <div style={{marginTop:8,fontSize:11,color:C.muted}}>No response reason logged —{" "}<button onClick={()=>setShowLog(true)} style={{background:"none",border:"none",color:C.amber,fontSize:11,cursor:"pointer",padding:0}}>+ Log what happened</button></div>
            )
          )}</div>}
          {e.outcome72h&&<div style={{marginTop:8,padding:"8px 12px",borderRadius:8,background:`${C.green}0a`,border:`1px solid ${C.green}25`,fontSize:12,color:C.green}}>72h outcome: {e.outcome72h}</div>}
          {e.notes&&<div style={{marginTop:10,fontSize:12,fontStyle:"italic",color:C.muted,borderLeft:`2px solid ${C.amber}50`,paddingLeft:10,lineHeight:1.6}}>"{e.notes}"</div>}
          {e.ongoing&&onResolve&&(
            <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
              <button onClick={()=>onResolve(e.submission_id||e.id)} style={{background:"none",border:`1px solid ${C.green}40`,borderRadius:8,padding:"7px 14px",color:C.green,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                ✓ Mark this crisis as resolved
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── SIGNAL JOURNAL WIRING ───────────────────────────────────────────────────
// Stable references so the journal's memoized reflections don't recompute.
const SJ_API = { url: SUPABASE_URL, anonKey: SUPABASE_ANON_KEY };
// Governed labels for anything Warrior-entered that goes into an exported page.
// Unknown or free-text values are summarized, never echoed (Codex correction 2).
const OTHER_TRIGGER = "Other (Warrior-described)";
const OTHER_ACTION = "Other (Warrior-described)";
const briefTriggerCounts = (entries) => {
  const c = {};
  entries.forEach((e) => {
    const seen = new Set((e.triggered || []).map((t) => triggerLabel(t) || OTHER_TRIGGER));
    seen.forEach((l) => { c[l] = (c[l] || 0) + 1; });
  });
  return Object.entries(c).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
};
const briefActions = (list) => [...new Set((list || []).map((t) => actionLabel(t) || OTHER_ACTION))];

// Community finding shown in the Clinical Brief by Jason's decision (2026-09-30),
// as a deliberate exception to governance packet §3. Self-reported "treatment is
// working" among ER visits with a recorded ending, Sept. 19, 2026 export (same
// figure as wip-site). An association, not a causal or clinical claim. Update
// only from an audited recount.
const TREATMENT_WORKING={admitted:20,admittedN:36,released:8,releasedN:30,asOf:"Sept. 19, 2026"};

// ── CLINICAL BRIEF GENERATOR ─────────────────────────────────────────────────
function generateBrief(entries){
  const sorted=[...entries].sort((a,b)=>new Date(a.submitted)-new Date(b.submitted));
  const latest=sorted[sorted.length-1];
  const triggerCounts=briefTriggerCounts(entries); // [[label,count],...] governed labels only
  const primaryTrigger=triggerCounts[0]?triggerCounts[0][0]:"—";
  const secondaryTrigger=triggerCounts[1]?triggerCounts[1][0]:null;
  const emergingTrigger=triggerCounts[2]?triggerCounts[2][0]:null;
  const hasMenstrual=entries.some(e=>e.triggered&&e.triggered.some(t=>t.includes("Menstrual")));
  const erVisits=entries.filter(e=>e.erVisit==="Yes");
  const violations=entries.filter(e=>e.erVisit==="Yes"&&(e.protocolFollowed==="No"||(e.whyNot&&e.whyNot.toLowerCase().match(/medical team|care team/))));
  const avgP=entries.map(e=>e.pain).filter(p=>p>0);
  const avg=avgP.length?(avgP.reduce((a,b)=>a+b,0)/avgP.length).toFixed(1):"—";
  const gaps=[];
  for(let i=1;i<sorted.length;i++) gaps.push(days(sorted[i-1].submitted,sorted[i].submitted));
  const longestGap=gaps.length?Math.max(...gaps):0;
  const today=new Date().toISOString().slice(0,10);
  const briefId=`HII-${String(entries[0].warriorId).replace(/[^a-zA-Z0-9]/g,'').slice(0,6).toUpperCase()}-${today.replace(/-/g,'')}-${String(entries.length).padStart(3,'0')}`;
  const daysSinceLast=days(sorted[sorted.length-1].submitted,today);
  const totalReports=entries.length;


  // Home treatment attempted (from latest entry)
  const homeTx=briefActions(latest.treatment).filter(t=>t!=="Prescription pain medication").join(", ");

  const erHistoryRows=erVisits.map(e=>{
    const viol=e.protocolFollowed==="No"||(e.whyNot&&e.whyNot.toLowerCase().match(/medical team|care team/));
    return `<tr>
      <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${esc(e.submitted)}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${esc(e.hospital)||"—"}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${e.waitHours?esc(e.waitHours)+"h":""}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;font-weight:600;color:${viol?"#dc2626":"#16a34a"};">${viol?"✗ NOT FOLLOWED":"✓ Followed"}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#555;">${viol&&e.whyNot?esc(e.whyNot):""}</td>
    </tr>`;
  }).join("");

  const html=`<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>Hii Clinical Intelligence Brief — ${esc(entries[0].warriorId)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:#111;background:#fff;padding:28px 32px;max-width:780px;margin:0 auto;font-size:13px;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #990000;padding-bottom:14px;margin-bottom:20px;}
  .brand{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:#888;margin-bottom:3px;}
  .title{font-size:20px;font-weight:800;color:#0f0f0f;letter-spacing:-0.3px;}
  .subtitle{font-size:12px;color:#555;margin-top:4px;line-height:1.6;}
  .section{margin-bottom:18px;page-break-inside:avoid;}
  .section-header{font-size:9px;letter-spacing:3px;text-transform:uppercase;color:#990000;font-weight:800;margin-bottom:8px;padding-bottom:5px;border-bottom:1.5px solid #fce8e8;display:flex;align-items:center;gap:6px;}
  .row{display:flex;gap:8px;margin-bottom:5px;align-items:baseline;}
  .label{font-size:11px;color:#777;min-width:170px;flex-shrink:0;}
  .value{font-size:12px;color:#111;font-weight:600;}
  .trigger-rank{display:flex;flex-direction:column;gap:6px;margin-top:6px;}
  .trigger-row{display:flex;align-items:center;gap:10px;}
  .trigger-badge{font-size:9px;letter-spacing:1px;text-transform:uppercase;padding:2px 7px;border-radius:3px;font-weight:700;flex-shrink:0;min-width:68px;text-align:center;}
  .primary-badge{background:#fef3c7;color:#92400e;border:1px solid #fde68a;}
  .secondary-badge{background:#f1f5f9;color:#475569;border:1px solid #cbd5e1;}
  .emerging-badge{background:#f0fdf4;color:#166534;border:1px solid #bbf7d0;}
  .trigger-name{font-size:12px;font-weight:600;color:#111;}
  .trigger-count{font-size:11px;color:#888;}
  .community-bar{display:flex;align-items:center;gap:8px;margin-top:8px;padding:8px 12px;background:#f9fafb;border-radius:6px;border:1px solid #e5e7eb;}
  table{width:100%;border-collapse:collapse;margin-top:8px;}
  th{background:#f9fafb;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#888;padding:7px 10px;text-align:left;border-bottom:2px solid #e5e7eb;}
  .safeguard-box{background:#fff1f2;border:1.5px solid #fecdd3;border-radius:6px;padding:12px 14px;font-size:12px;color:#7f1d1d;line-height:1.7;margin-top:10px;}
  .request-box{background:#f0f9ff;border:1.5px solid #bae6fd;border-radius:6px;padding:14px 16px;font-size:12px;line-height:1.8;color:#0c4a6e;}
  .cost-box{background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:10px 14px;font-size:11px;color:#78350f;line-height:1.7;margin-top:10px;}
  .footer{margin-top:20px;padding-top:12px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:flex-end;}
  .footer-left{font-size:10px;color:#aaa;line-height:1.8;}
  .footer-right{text-align:right;}
  .tagline{font-size:11px;color:#C1A004;font-weight:700;letter-spacing:.5px;}
  .copyright{font-size:9px;color:#bbb;margin-top:2px;}
  .menstrual-flag{background:#fdf4ff;border:1px solid #e9d5ff;border-radius:5px;padding:7px 12px;font-size:11px;color:#6b21a8;margin-top:8px;line-height:1.6;}
  @media print{body{padding:16px 20px;} .no-print{display:none;} .section{page-break-inside:avoid;}}
</style>
</head>
<body>

<div class="header">
  <div>
    <div class="brand">Human Intelligence Infrastructure · Warrior Intelligence Project</div>
    <div class="title">Hii Clinical Intelligence Brief</div>
    <div class="subtitle">
      Warrior ID: <strong>${esc(entries[0].warriorId)}</strong> &nbsp;·&nbsp; Generated: ${today}<br/>
      ${totalReports} crisis report${totalReports!==1?"s":""} on record &nbsp;·&nbsp; Tracking for: ${esc(entries[0].trackingFor)}
    </div>
    <div style="font-size:9px;color:#bbb;margin-top:3px;font-family:monospace;letter-spacing:1.5px;">Brief ID: ${briefId}</div>
  </div>
  <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px;">
    <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&color=990000&bgcolor=ffffff&data=https://warrior-intelligence-dashboard.vercel.app" width="72" height="72" style="border:1px solid #e5e7eb;border-radius:4px;display:block;" alt="QR"/>
    <div style="font-size:8px;color:#aaa;text-align:right;margin-top:2px;">Scan to verify live data</div>
  </div>
</div>

<div class="section">
  <div class="section-header">◈ &nbsp;Compass &nbsp;—&nbsp; Longitudinal Orientation</div>
  <div class="row"><span class="label">Baseline stability window</span><span class="value">${daysSinceLast} days since last crisis report</span></div>
  ${longestGap>0?`<div class="row"><span class="label">Longest recorded window</span><span class="value">${longestGap} days</span></div>`:""}
  <div class="row"><span class="label">Current trajectory</span><span class="value">${latest.crisisStart?`Started ${esc(latest.crisisStart)}`:""} ${latest.ongoing?"— Active crisis":"— Resolved"}</span></div>
  <div class="row"><span class="label">Pain intensity (last report)</span><span class="value">${esc(latest.pain)}/10 &nbsp;·&nbsp; ${esc(normPC(latest.painCompared)||"")}</span></div>
  <div class="row"><span class="label">Average pain (all reports)</span><span class="value">${avg}/10</span></div>
  ${homeTx?`<div class="row"><span class="label">Home management attempted</span><span class="value">${esc(homeTx)}</span></div>`:""}
  ${latest.hydrationOz!=null?`<div class="row"><span class="label">Pre-crisis hydration (logged)</span><span class="value">${esc(latest.hydrationOz)}oz in 24hrs prior${latest.hydrationOz<40?' <span style="color:#dc2626;font-size:11px;">— below threshold</span>':latest.hydrationOz>=64?' <span style="color:#16a34a;font-size:11px;">— adequate</span>':' <span style="color:#d97706;font-size:11px;">— borderline</span>'}</span></div>`:""}
  ${latest.working?`<div class="row"><span class="label">Home treatment response (patient-reported)</span><span class="value">${esc(latest.working)}</span></div>`:""}
  <div style="margin-top:8px;padding:8px 12px;background:#f8fafc;border-radius:5px;border:1px solid #e2e8f0;font-size:11px;color:#334155;line-height:1.7;">
    <strong>Clinical context:</strong> This patient is an expert navigator of their own maintenance pattern.
    ${latest.ongoing?"The patient reports this crisis is still ongoing.":"Crisis resolved. This brief documents the completed episode for clinical record."}
  </div>
</div>

<div class="section">
  <div class="section-header">◈ &nbsp;Loop &nbsp;—&nbsp; Documented Trigger Patterns (patient-reported)</div>
  <div class="trigger-rank">
    ${triggerCounts[0]?`<div class="trigger-row"><span class="trigger-badge primary-badge">Primary</span><span class="trigger-name">${esc(triggerCounts[0][0])}</span><span class="trigger-count">(${triggerCounts[0][1]} of ${totalReports} report${totalReports!==1?"s":""})</span></div>`:""}
    ${triggerCounts[1]?`<div class="trigger-row"><span class="trigger-badge secondary-badge">Secondary</span><span class="trigger-name">${esc(triggerCounts[1][0])}</span><span class="trigger-count">(${triggerCounts[1][1]} of ${totalReports} report${totalReports!==1?"s":""})</span></div>`:""}
    ${triggerCounts[2]?`<div class="trigger-row"><span class="trigger-badge emerging-badge">Emerging</span><span class="trigger-name">${esc(triggerCounts[2][0])}</span><span class="trigger-count">(${triggerCounts[2][1]} of ${totalReports} report${totalReports!==1?"s":""})</span></div>`:""}
  </div>
  ${hasMenstrual?`<div class="menstrual-flag">⚠ <strong>Hormonal Trigger Identified:</strong> Menstrual cycle is a documented crisis trigger for this patient — as recorded in their own entries.</div>`:""}
  ${latest.treatment.length?`<div class="row" style="margin-top:10px;"><span class="label">Current treatment</span><span class="value">${esc(briefActions(latest.treatment).join(", "))}</span></div>`:""}
</div>

<div class="section">
  <div class="section-header">◈ &nbsp;Safeguard &nbsp;—&nbsp; Protocol & Accountability History</div>
  ${erVisits.length?`
  <table>
    <thead><tr><th>Date</th><th>Hospital</th><th>Wait</th><th>Protocol Status</th><th>Reason (if not followed)</th></tr></thead>
    <tbody>${erHistoryRows}</tbody>
  </table>`:`<div style="font-size:12px;color:#777;padding:6px 0;">No ER visits on record for this Warrior.</div>`}
  ${violations.length?`<div class="safeguard-box" style="margin-top:10px;"><strong>${violations.length} documented protocol violation${violations.length!==1?"s":""} on record.</strong> These encounters are logged in the Warrior Intelligence Project community evidence base — maintained by Sickle Cell Warriors of Buffalo for quality improvement reporting, health equity advocacy, and institutional partnership conversations.</div>`:""}
</div>

<div class="section">
  <div class="section-header">◈ &nbsp;Clinical Request</div>
  <div class="request-box">
    This patient has documented expertise in managing their own crisis trajectory. Home management has been attempted${latest.working?` (patient-reported response: ${esc(latest.working)})`:""}. <strong>Please initiate established Sickle Cell pain protocol immediately.</strong><br/><br/>
    In WIP community data, Warriors who were admitted were about <strong>2× as likely</strong> to report their treatment was working (${TREATMENT_WORKING.admitted} of ${TREATMENT_WORKING.admittedN} admitted vs. ${TREATMENT_WORKING.released} of ${TREATMENT_WORKING.releasedN} treated and released; ${TREATMENT_WORKING.asOf} export). Community-reported, not a clinical determination. <strong>This patient is asking for the established protocol to start now and for admission to be considered.</strong>
  </div>
  <div class="cost-box">
    <strong>Systemic Risk & Cost Context:</strong> Research (Shah et al., 2020) estimates mean annual SCD-related costs at <strong>~$59,000 per patient</strong> — largely driven by acute crisis mismanagement and ineffective treat-and-release cycles. Protocol adherence at this visit directly affects that trajectory.
  </div>
</div>

<div class="footer">
  <div class="footer-left">
    Warrior Intelligence Project &nbsp;·&nbsp; Sickle Cell Warriors of Buffalo<br/>
    info@kindredcompassholdings.com &nbsp;·&nbsp; 716-818-2338<br/>
    warrior-intelligence-dashboard.vercel.app
  </div>
  <div class="footer-right">
    <div class="tagline">Our Pain. Our Data. Our Power.</div>
    <div class="copyright">© ${new Date().getFullYear()} Jason Robert Moore. All Rights Reserved.<br/>Human Intelligence Infrastructure (Hii) Framework.</div>
  </div>
</div>

</body>
</html>`;

  // Extract just the body content for inline rendering
  const bodyMatch=html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const styleMatch=html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const inlineHTML=(styleMatch?`<style>${styleMatch[1]}</style>`:"")+
    (bodyMatch?bodyMatch[1]:html);
  showPrintDoc(inlineHTML);
}

// ── PROTOCOL BUILDER ─────────────────────────────────────────────────────────
function ProtocolBuilder({entries, onClose}){
  const [form,setForm]=useState({
    preferredMeds:"",
    stepUpMeds:"",
    allergies:"",
    hematologistName:"",
    hematologistPhone:"",
    emergencyContact:"",
    emergencyPhone:"",
    additionalNotes:"",
  });

  const sorted=[...entries].sort((a,b)=>new Date(a.submitted)-new Date(b.submitted));
  const avgP=entries.map(e=>e.pain).filter(p=>p>0);
  const avgPain=avgP.length?(avgP.reduce((a,b)=>a+b,0)/avgP.length).toFixed(1):"—";
  const triggerCounts=briefTriggerCounts(entries);
  const topTriggers=triggerCounts.slice(0,3).map(([t])=>t);
  const effectiveTx=entries.filter(e=>e.working==="Well"||e.working==="Moderately").flatMap(e=>e.treatment);
  const ineffectiveTx=entries.filter(e=>e.working==="Not at all").flatMap(e=>e.treatment);
  const uniqueEffective=briefActions(effectiveTx.filter(t=>!String(t).includes("Nothing")));
  const uniqueIneffective=briefActions(ineffectiveTx.filter(t=>!String(t).includes("Nothing")));
  const today=new Date().toISOString().slice(0,10);

  function update(k,v){setForm(f=>({...f,[k]:v}));}

  function generateProtocol(){
    const html=`<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>SCD Pain Protocol Request — ${esc(entries[0].warriorId)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:#111;background:#fff;padding:28px 32px;max-width:780px;margin:0 auto;font-size:13px;}
  .header{border-bottom:3px solid #990000;padding-bottom:14px;margin-bottom:20px;}
  .brand{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:#888;margin-bottom:3px;}
  .title{font-size:20px;font-weight:800;color:#0f0f0f;letter-spacing:-0.3px;}
  .subtitle{font-size:12px;color:#555;margin-top:4px;line-height:1.6;}
  .intro-box{background:#f0f9ff;border:1.5px solid #bae6fd;border-radius:6px;padding:14px 16px;font-size:12px;line-height:1.8;color:#0c4a6e;margin-bottom:20px;}
  .section{margin-bottom:18px;page-break-inside:avoid;}
  .section-header{font-size:9px;letter-spacing:3px;text-transform:uppercase;color:#990000;font-weight:800;margin-bottom:8px;padding-bottom:5px;border-bottom:1.5px solid #fce8e8;}
  .row{display:flex;gap:8px;margin-bottom:5px;align-items:baseline;}
  .label{font-size:11px;color:#777;min-width:180px;flex-shrink:0;}
  .value{font-size:12px;color:#111;font-weight:600;}
  .tag{display:inline-block;padding:2px 9px;border-radius:12px;font-size:11px;margin-right:4px;margin-bottom:3px;}
  .trigger-tag{background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;}
  .effective-tag{background:#f0fdf4;color:#166534;border:1px solid #bbf7d0;}
  .ineffective-tag{background:#fff1f2;color:#7f1d1d;border:1px solid #fecdd3;}
  .sign-box{margin-top:28px;padding:16px;border:1.5px dashed #d1d5db;border-radius:6px;background:#fafafa;}
  .sign-row{display:flex;gap:40px;margin-top:16px;}
  .sign-field{flex:1;}
  .sign-line{border-bottom:1px solid #374151;margin-top:24px;margin-bottom:4px;}
  .sign-label{font-size:10px;color:#6b7280;letter-spacing:.5px;}
  .fill-line{border-bottom:1px solid #9ca3af;min-width:200px;display:inline-block;margin-left:8px;margin-bottom:2px;}
  .fill-section{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:14px 16px;margin-top:8px;}
  .fill-row{display:flex;align-items:baseline;gap:8px;margin-bottom:10px;}
  .fill-label{font-size:11px;color:#374151;min-width:160px;flex-shrink:0;font-weight:500;}
  .fill-value{font-size:12px;color:#111;font-weight:600;border-bottom:1px solid #d1d5db;flex:1;padding-bottom:2px;min-height:18px;}
  .footer{margin-top:20px;padding-top:12px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:flex-end;}
  .tagline{font-size:11px;color:#C1A004;font-weight:700;letter-spacing:.5px;}
  .copyright{font-size:9px;color:#bbb;margin-top:2px;}
  @media print{body{padding:16px 20px;} .no-print{display:none;}}
</style>
</head>
<body>

<div class="header">
  <div class="brand">Human Intelligence Infrastructure · Warrior Intelligence Project · Sickle Cell Warriors of Buffalo</div>
  <div class="title">Sickle Cell Disease Pain Protocol Request</div>
  <div class="subtitle">Prepared for clinical review &nbsp;·&nbsp; Warrior ID: <strong>${esc(entries[0].warriorId)}</strong> &nbsp;·&nbsp; Generated: ${today}</div>
</div>

<div class="intro-box">
  <strong>To the treating clinician:</strong> This document has been prepared by the patient using the Warrior Intelligence Project — a community-owned, real-time SCD crisis tracking system maintained by Sickle Cell Warriors of Buffalo. The patient is requesting that you review the information below, adapt as clinically appropriate, and establish a signed SCD Pain Protocol for use in future emergency visits. 
</div>

<div class="section">
  <div class="section-header">◈ &nbsp;Patient Baseline — From Crisis Tracking Data</div>
  <div class="row"><span class="label">Warrior ID</span><span class="value">${esc(entries[0].warriorId)}</span></div>
  <div class="row"><span class="label">Crisis reports on record</span><span class="value">${entries.length} report${entries.length!==1?"s":""}</span></div>
  <div class="row"><span class="label">Average pain level at crisis</span><span class="value">${avgPain}/10</span></div>
  <div class="row"><span class="label">Tracking for</span><span class="value">${esc(entries[0].trackingFor)}</span></div>
  ${topTriggers.length?`
  <div style="margin-top:8px;">
    <div class="label" style="margin-bottom:5px;">Documented primary triggers</div>
    ${topTriggers.map(t=>`<span class="tag trigger-tag">${esc(t)}</span>`).join("")}
  </div>`:""}
  ${uniqueEffective.length?`
  <div style="margin-top:8px;">
    <div class="label" style="margin-bottom:5px;">Treatments with documented effectiveness</div>
    ${uniqueEffective.map(t=>`<span class="tag effective-tag">${esc(t)}</span>`).join("")}
  </div>`:""}
  ${uniqueIneffective.length?`
  <div style="margin-top:8px;">
    <div class="label" style="margin-bottom:5px;">Treatments with no documented effectiveness</div>
    ${uniqueIneffective.map(t=>`<span class="tag ineffective-tag">${esc(t)}</span>`).join("")}
  </div>`:""}
</div>

<div class="section">
  <div class="section-header">◈ &nbsp;Patient-Provided Information</div>
  <div class="fill-section">
    <div class="fill-row"><span class="fill-label">Preferred pain medication(s)</span><span class="fill-value">${esc(form.preferredMeds)}</span></div>
    <div class="fill-row"><span class="fill-label">Step-up medication(s)</span><span class="fill-value">${esc(form.stepUpMeds)}</span></div>
    <div class="fill-row"><span class="fill-label">Known allergies</span><span class="fill-value">${esc(form.allergies)||"None reported"}</span></div>
    <div class="fill-row"><span class="fill-label">Hematologist / SCD specialist</span><span class="fill-value">${esc(form.hematologistName)}</span></div>
    <div class="fill-row"><span class="fill-label">Specialist phone</span><span class="fill-value">${esc(form.hematologistPhone)}</span></div>
    <div class="fill-row"><span class="fill-label">Emergency contact</span><span class="fill-value">${esc(form.emergencyContact)}</span></div>
    <div class="fill-row"><span class="fill-label">Emergency contact phone</span><span class="fill-value">${esc(form.emergencyPhone)}</span></div>
    ${form.additionalNotes?`<div class="fill-row"><span class="fill-label">Additional notes</span><span class="fill-value">${esc(form.additionalNotes)}</span></div>`:""}
  </div>
</div>

<div class="section">
  <div class="section-header">◈ &nbsp;Protocol to Be Established (Clinician Completes)</div>
  <div class="fill-section">
    <div class="fill-row"><span class="fill-label">First-line pain medication</span><span class="fill-value">&nbsp;</span></div>
    <div class="fill-row"><span class="fill-label">Dose and route</span><span class="fill-value">&nbsp;</span></div>
    <div class="fill-row"><span class="fill-label">Reassessment interval</span><span class="fill-value">&nbsp;</span></div>
    <div class="fill-row"><span class="fill-label">Step-up if inadequate relief</span><span class="fill-value">&nbsp;</span></div>
    <div class="fill-row"><span class="fill-label">Admission threshold</span><span class="fill-value">&nbsp;</span></div>
    <div class="fill-row"><span class="fill-label">IV fluids</span><span class="fill-value">&nbsp;</span></div>
    <div class="fill-row"><span class="fill-label">Other standing orders</span><span class="fill-value">&nbsp;</span></div>
  </div>
</div>

<div class="sign-box">
  <div style="font-size:11px;color:#374151;font-weight:600;margin-bottom:4px;">Clinician Authorization</div>
  <div style="font-size:11px;color:#6b7280;line-height:1.7;">By signing below, the clinician confirms they have reviewed this protocol request, made appropriate clinical adaptations, and established this as the patient's standing SCD pain management protocol.</div>
  <div class="sign-row">
    <div class="sign-field">
      <div class="sign-line"></div>
      <div class="sign-label">Clinician Signature</div>
    </div>
    <div class="sign-field">
      <div class="sign-line"></div>
      <div class="sign-label">Printed Name & Title</div>
    </div>
    <div class="sign-field">
      <div class="sign-line"></div>
      <div class="sign-label">Date</div>
    </div>
    <div class="sign-field">
      <div class="sign-line"></div>
      <div class="sign-label">NPI / License #</div>
    </div>
  </div>
</div>

<div class="footer">
  <div style="font-size:10px;color:#aaa;line-height:1.8;">
    Warrior Intelligence Project &nbsp;·&nbsp; Sickle Cell Warriors of Buffalo<br/>
    info@kindredcompassholdings.com &nbsp;·&nbsp; 716-818-2338<br/>
    warrior-intelligence-dashboard.vercel.app
  </div>
  <div style="text-align:right;">
    <div class="tagline">Our Pain. Our Data. Our Power.</div>
    <div class="copyright">© ${new Date().getFullYear()} Jason Robert Moore. All Rights Reserved.<br/>Human Intelligence Infrastructure (Hii) Framework.</div>
  </div>
</div>

</body>
</html>`;

    const bodyMatch=html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    const styleMatch=html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    const inlineHTML=(styleMatch?`<style>${styleMatch[1]}</style>`:"")+
      (bodyMatch?bodyMatch[1]:html);
    showPrintDoc(inlineHTML);
  }

  const inputStyle={width:"100%",background:C.surf,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",color:C.text,fontSize:13,outline:"none",fontFamily:"'IBM Plex Sans',sans-serif"};
  const labelStyle={fontSize:11,color:C.muted,letterSpacing:.5,marginBottom:5,display:"block"};

  return(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.85)",zIndex:100,overflowY:"auto",padding:"24px 16px"}}>
      <div style={{maxWidth:600,margin:"0 auto",background:C.surf,borderRadius:16,border:`1px solid ${C.border}`,padding:28}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <div style={{fontSize:10,letterSpacing:2.5,color:C.red,textTransform:"uppercase",marginBottom:3}}>Protocol Builder</div>
            <div style={{fontFamily:"Syne",fontWeight:800,fontSize:18}}>SCD Pain Protocol Request</div>
            <div style={{fontSize:12,color:C.muted,marginTop:3}}>This document goes to your doctor to establish your ER protocol.</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",padding:4}}>✕</button>
        </div>

        <div style={{background:`${C.teal}0a`,border:`1px solid ${C.teal}20`,borderRadius:10,padding:"12px 16px",marginBottom:20,fontSize:12,color:"rgba(255,255,255,0.7)",lineHeight:1.7}}>
          <span style={{color:C.teal,fontWeight:600}}>Pre-filled from your tracker data:</span> average pain {avgPain}/10, top triggers ({topTriggers.join(", ")||"—"}){uniqueEffective.length?`, effective treatments (${uniqueEffective.slice(0,2).join(", ")})`:""}.
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div>
            <label style={labelStyle}>Preferred pain medication(s) *</label>
            <input style={inputStyle} placeholder="e.g. IV Dilaudid 1mg, oral oxycodone" value={form.preferredMeds} onChange={e=>update("preferredMeds",e.target.value)}/>
          </div>
          <div>
            <label style={labelStyle}>Step-up medication(s) if first-line fails</label>
            <input style={inputStyle} placeholder="e.g. Morphine PCA, IV ketamine" value={form.stepUpMeds} onChange={e=>update("stepUpMeds",e.target.value)}/>
          </div>
          <div>
            <label style={labelStyle}>Known allergies or medications to avoid</label>
            <input style={inputStyle} placeholder="e.g. NSAIDS, codeine, penicillin" value={form.allergies} onChange={e=>update("allergies",e.target.value)}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <label style={labelStyle}>Hematologist / SCD specialist name</label>
              <input style={inputStyle} placeholder="Dr. Name" value={form.hematologistName} onChange={e=>update("hematologistName",e.target.value)}/>
            </div>
            <div>
              <label style={labelStyle}>Specialist phone number</label>
              <input style={inputStyle} placeholder="(000) 000-0000" value={form.hematologistPhone} onChange={e=>update("hematologistPhone",e.target.value)}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <label style={labelStyle}>Emergency contact name</label>
              <input style={inputStyle} placeholder="Full name" value={form.emergencyContact} onChange={e=>update("emergencyContact",e.target.value)}/>
            </div>
            <div>
              <label style={labelStyle}>Emergency contact phone</label>
              <input style={inputStyle} placeholder="(000) 000-0000" value={form.emergencyPhone} onChange={e=>update("emergencyPhone",e.target.value)}/>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Additional notes for your doctor (optional)</label>
            <textarea style={{...inputStyle,minHeight:72,resize:"vertical"}} placeholder="Anything else your doctor should know..." value={form.additionalNotes} onChange={e=>update("additionalNotes",e.target.value)}/>
          </div>
        </div>

        <div style={{display:"flex",gap:10,marginTop:22}}>
          <button onClick={onClose} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 0",color:C.muted,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={generateProtocol} style={{flex:2,background:C.red,border:"none",borderRadius:8,padding:"11px 0",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer"}}>Generate Protocol Request →</button>
        </div>

        <div style={{marginTop:14,fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.6}}>
          Nothing you enter here is stored or transmitted. This generates a PDF for you to print or save.
        </div>
      </div>
    </div>
  );
}

// ── SHOW ED MODE ─────────────────────────────────────────────────────────────
function EDMode({entries, onClose}){
  const sorted=[...entries].sort((a,b)=>new Date(a.submitted)-new Date(b.submitted));
  const latest=sorted[sorted.length-1];
  const triggerCounts=briefTriggerCounts(entries);
  const topTriggers=triggerCounts.slice(0,3).map(([t])=>t);
  const violations=entries.filter(e=>e.erVisit==="Yes"&&(e.protocolFollowed==="No"||(e.whyNot&&e.whyNot.toLowerCase().match(/medical team|care team/))));
  const avgP=entries.map(e=>e.pain).filter(p=>p>0);
  const avg=avgP.length?(avgP.reduce((a,b)=>a+b,0)/avgP.length).toFixed(1):"—";
  const today=new Date().toISOString().slice(0,10);
  const homeFailed=latest.working==="Not at all"||latest.working==="A little";
  const hasMenstrual=entries.some(e=>e.triggered&&e.triggered.some(t=>t.includes("Menstrual")));

  // Stability
  const gaps=[];
  for(let i=1;i<sorted.length;i++) gaps.push(days(sorted[i-1].submitted,sorted[i].submitted));
  const daysSinceLast=days(sorted[sorted.length-1].submitted,today);
  const pains=sorted.map(e=>e.pain).filter(p=>p>0);
  const firstHalf=pains.slice(0,Math.floor(pains.length/2));
  const secondHalf=pains.slice(Math.floor(pains.length/2));
  const avgFirst=firstHalf.length?firstHalf.reduce((a,b)=>a+b,0)/firstHalf.length:5;
  const avgSecond=secondHalf.length?secondHalf.reduce((a,b)=>a+b,0)/secondHalf.length:5;
  const painDelta=avgSecond-avgFirst;
  const avgGap=gaps.length?gaps.reduce((a,b)=>a+b,0)/gaps.length:30;
  const recentERRate=entries.slice(-3).filter(e=>e.erVisit==="Yes").length/Math.min(entries.length,3);
  let score=60;
  if(painDelta<-1)score+=15;else if(painDelta>1)score-=15;
  if(avgGap>21)score+=15;else if(avgGap<7)score-=15;
  if(!latest.ongoing)score+=10;else score-=10;
  if(recentERRate<0.33)score+=10;else if(recentERRate>0.66)score-=10;
  if(!latest.ongoing&&daysSinceLast>30)score+=20;
  else if(!latest.ongoing&&daysSinceLast>14)score+=10;
  score=Math.max(10,Math.min(95,score));
  const statusLabel=score>=65?"STABLE":score>=40?"WATCHFUL":"HIGH LOAD";
  const statusColor=score>=65?C.green:score>=40?C.amber:C.red;

  return(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"#fff",zIndex:200,overflowY:"auto",padding:"32px 28px",color:"#111",fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif"}}>

      {/* Close button */}
      <button onClick={onClose} className="no-print" style={{position:"fixed",top:16,right:16,background:"#f3f4f6",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,cursor:"pointer",color:"#374151",fontWeight:500,zIndex:201}}>✕ Exit ED Mode</button>

      {/* Header */}
      <div style={{borderBottom:"3px solid #990000",paddingBottom:16,marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{fontSize:11,letterSpacing:3,textTransform:"uppercase",color:"#888",marginBottom:4}}>Human Intelligence Infrastructure · Warrior Intelligence Project</div>
          <div style={{fontSize:28,fontWeight:800,color:"#0f0f0f",letterSpacing:-0.5,lineHeight:1.1}}>Hii Clinical Intelligence Brief</div>
          <div style={{fontSize:14,color:"#555",marginTop:6}}>Warrior ID: <strong>{entries[0].warriorId}</strong> &nbsp;·&nbsp; {today} &nbsp;·&nbsp; {entries.length} report{entries.length!==1?"s":""} on record</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
          <div style={{padding:"12px 24px",borderRadius:8,fontWeight:800,fontSize:18,letterSpacing:2,textTransform:"uppercase",background:`${statusColor}15`,color:statusColor,border:`2px solid ${statusColor}50`}}>{statusLabel}</div>
          <div style={{fontSize:10,color:"#aaa",textAlign:"right"}}>warrior-intelligence-dashboard.vercel.app</div>
        </div>
      </div>

      {/* Clinical Request — FIRST AND PROMINENT */}
      <div style={{background:"#f0f9ff",border:"2px solid #0ea5e9",borderRadius:12,padding:"20px 24px",marginBottom:28}}>
        <div style={{fontSize:11,letterSpacing:3,textTransform:"uppercase",color:"#0369a1",fontWeight:700,marginBottom:10}}>◈ Clinical Request</div>
        <div style={{fontSize:18,fontWeight:700,color:"#0c4a6e",lineHeight:1.6,marginBottom:12}}>
          Please initiate established Sickle Cell pain protocol immediately.
        </div>
        <div style={{fontSize:15,color:"#0c4a6e",lineHeight:1.7}}>
          Home management has been attempted{latest.working?<> (patient-reported response: {latest.working})</>:""}.
        </div>
        {violations.length>0&&(
          <div style={{marginTop:12,padding:"10px 14px",background:"#fff1f2",border:"1px solid #fecdd3",borderRadius:8,fontSize:14,color:"#7f1d1d",fontWeight:600}}>
            ⚠ {violations.length} documented protocol violation{violations.length>1?"s":""} on record at prior visits.
          </div>
        )}
      </div>

      {/* Three columns */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:20,marginBottom:28}}>

        {/* Compass */}
        <div style={{border:"1.5px solid #e5e7eb",borderRadius:10,padding:18}}>
          <div style={{fontSize:10,letterSpacing:2.5,textTransform:"uppercase",color:"#990000",fontWeight:700,marginBottom:12,paddingBottom:6,borderBottom:"1px solid #fce8e8"}}>◈ Compass</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div><div style={{fontSize:11,color:"#888"}}>Crisis reports on record</div><div style={{fontSize:20,fontWeight:800,color:"#111"}}>{entries.length}</div></div>
            <div><div style={{fontSize:11,color:"#888"}}>Average pain level</div><div style={{fontSize:20,fontWeight:800,color:"#dc2626"}}>{avg}/10</div></div>
            <div><div style={{fontSize:11,color:"#888"}}>Pain (last report)</div><div style={{fontSize:20,fontWeight:800,color:"#dc2626"}}>{latest.pain}/10</div></div>
            <div><div style={{fontSize:11,color:"#888"}}>Stability window</div><div style={{fontSize:16,fontWeight:700,color:"#111"}}>{daysSinceLast} days</div></div>
            <div><div style={{fontSize:11,color:"#888"}}>Current status</div><div style={{fontSize:13,fontWeight:600,color:latest.ongoing?"#dc2626":"#16a34a"}}>{latest.ongoing?"Active crisis":"Resolved"}</div></div>
            {latest.hydrationOz&&<div><div style={{fontSize:11,color:"#888"}}>Pre-crisis hydration</div><div style={{fontSize:14,fontWeight:700,color:latest.hydrationOz<40?"#dc2626":latest.hydrationOz>=64?"#16a34a":"#d97706"}}>{latest.hydrationOz}oz</div></div>}
          </div>
        </div>

        {/* Loop */}
        <div style={{border:"1.5px solid #e5e7eb",borderRadius:10,padding:18}}>
          <div style={{fontSize:10,letterSpacing:2.5,textTransform:"uppercase",color:"#990000",fontWeight:700,marginBottom:12,paddingBottom:6,borderBottom:"1px solid #fce8e8"}}>◈ Loop — Triggers</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {triggerCounts.slice(0,3).map(([t,count],i)=>(
              <div key={t} style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",padding:"3px 8px",borderRadius:4,fontWeight:700,background:i===0?"#fef3c7":i===1?"#f1f5f9":"#f0fdf4",color:i===0?"#92400e":i===1?"#475569":"#166534",border:`1px solid ${i===0?"#fde68a":i===1?"#cbd5e1":"#bbf7d0"}`,flexShrink:0}}>{i===0?"Primary":i===1?"Secondary":"Emerging"}</span>
                <span style={{fontSize:14,fontWeight:700,color:"#111"}}>{t}</span>
                <span style={{fontSize:11,color:"#888",marginLeft:"auto"}}>{count}/{entries.length}</span>
              </div>
            ))}
            {hasMenstrual&&<div style={{marginTop:6,padding:"8px 10px",background:"#fdf4ff",border:"1px solid #e9d5ff",borderRadius:6,fontSize:12,color:"#6b21a8",fontWeight:600}}>⚠ Menstrual cycle — documented trigger</div>}
          </div>
          {latest.treatment.length>0&&(
            <div style={{marginTop:14,paddingTop:10,borderTop:"1px solid #f3f4f6"}}>
              <div style={{fontSize:11,color:"#888",marginBottom:4}}>Home management attempted</div>
              <div style={{fontSize:13,color:"#111",lineHeight:1.6}}>{latest.treatment.filter(t=>!t.includes("Nothing")).join(", ")||"—"}</div>
              {latest.working&&<div style={{fontSize:12,color:homeFailed?"#dc2626":"#16a34a",fontWeight:600,marginTop:4}}>Response: {latest.working}</div>}
            </div>
          )}
        </div>

        {/* Safeguard */}
        <div style={{border:"1.5px solid #e5e7eb",borderRadius:10,padding:18}}>
          <div style={{fontSize:10,letterSpacing:2.5,textTransform:"uppercase",color:"#990000",fontWeight:700,marginBottom:12,paddingBottom:6,borderBottom:"1px solid #fce8e8"}}>◈ Safeguard</div>
          {entries.filter(e=>e.erVisit==="Yes").length>0?(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {entries.filter(e=>e.erVisit==="Yes").map((e,i)=>{
                const viol=e.protocolFollowed==="No"||(e.whyNot&&e.whyNot.toLowerCase().match(/medical team|care team/));
                return(
                  <div key={i} style={{padding:"8px 10px",borderRadius:6,background:viol?"#fff1f2":"#f0fdf4",border:`1px solid ${viol?"#fecdd3":"#bbf7d0"}`}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#111"}}>{e.hospital||"ER Visit"}</div>
                    <div style={{fontSize:11,color:"#555"}}>{e.submitted}</div>
                    <div style={{fontSize:12,fontWeight:600,color:viol?"#dc2626":"#16a34a",marginTop:2}}>{viol?"✗ Protocol NOT followed":"✓ Protocol followed"}</div>
                  </div>
                );
              })}
            </div>
          ):(
            <div style={{fontSize:13,color:"#888"}}>No ER visits on record.</div>
          )}
          <div style={{marginTop:14,paddingTop:10,borderTop:"1px solid #f3f4f6",fontSize:11,color:"#555",lineHeight:1.6}}>
            Data verified by Sickle Cell Warriors of Buffalo · Community evidence base
          </div>
        </div>
      </div>

      {/* Cost context */}
      <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"14px 18px",marginBottom:24,fontSize:13,color:"#78350f",lineHeight:1.7}}>
        <strong>Systemic Risk & Cost Context:</strong> Shah et al. (2020) estimates mean annual SCD-related costs at <strong>~$59,000 per patient</strong> — largely driven by ineffective treat-and-release cycles. Protocol adherence at this visit directly affects that trajectory.
      </div>

      {/* Footer */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",paddingTop:14,borderTop:"1px solid #e5e7eb"}}>
        <div style={{fontSize:11,color:"#aaa",lineHeight:1.8}}>
          Warrior Intelligence Project · Sickle Cell Warriors of Buffalo<br/>
          info@kindredcompassholdings.com · 716-818-2338<br/>
          warrior-intelligence-dashboard.vercel.app
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:12,color:"#C1A004",fontWeight:700,letterSpacing:.5}}>Our Pain. Our Data. Our Power.</div>
          <div style={{fontSize:9,color:"#bbb",marginTop:2}}>© {new Date().getFullYear()} Jason Robert Moore. All Rights Reserved. Human Intelligence Infrastructure (Hii) Framework.</div>
        </div>
      </div>

    </div>
  );
}

// ── WARRIOR PROFILE ───────────────────────────────────────────────────────────
function WarriorProfile({entries,onReset,postSubmit,allData,authSession}){
  const [localEntries,setLocalEntries]=useState(entries);
  const [showProtocolBuilder,setShowProtocolBuilder]=useState(false);
  const [showEDMode,setShowEDMode]=useState(false);
  const [showConfirmation,setShowConfirmation]=useState(!!postSubmit);
  const [showCheckIn,setShowCheckIn]=useState(false);
  const [checkInKey,setCheckInKey]=useState(0); // increment to refresh history
  const isOwnProfile=authSession&&authSession.warriorId&&entries[0]&&authSession.warriorId===entries[0].warriorId;
  const sorted=[...localEntries].sort((a,b)=>new Date(a.submitted)-new Date(b.submitted));
  const topT=countT(localEntries).slice(0,3).map(([t])=>t);
  const erCount=localEntries.filter(e=>e.erVisit==="Yes").length;
  const violCount=localEntries.filter(e=>e.erVisit==="Yes"&&(e.protocolFollowed==="No"||(e.whyNot&&e.whyNot.toLowerCase().match(/medical team|care team/)))).length;
  const hasMenstrual=localEntries.some(e=>e.triggered.some(t=>t.includes("Menstrual")));
  const spanDays=localEntries.length>1?days(sorted[0].submitted,sorted[sorted.length-1].submitted):0;

  function exportMyData(){
    if(!window.confirm("This file will contain your personal health data — crisis reports, hospital visits, and notes. Save it somewhere secure and do not share it publicly. Continue with download?"))return;
    const headers=["submitted","crisisStart","pain","painCompared","painLocations","triggered","erVisit","hospital","waitHours","protocolFollowed","whyNot","treatment","working","admitted","outcome72h","notes"];
    const rows=localEntries.map(e=>headers.map(k=>JSON.stringify(Array.isArray(e[k])?e[k].join("; "):e[k]??"",-1)).join(","));
    const csv=[headers.join(","),...rows].join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url;a.download=`${localEntries[0].warriorId}-data.csv`;
    a.click();URL.revokeObjectURL(url);
  }
  function handleLogViolation(entryId, whyNot){
    setLocalEntries(prev=>prev.map(e=>(e.id||e.submission_id)===entryId?{...e,whyNot}:e));
  }
  async function handleResolve(submissionId){
    if(!authSession?.accessToken) return; // must be signed in
    try{
      const result = await resolveCrisis(submissionId, authSession.accessToken);
      if(result.success || result.alreadyResolved){
        // Optimistic update — mark ongoing false locally
        setLocalEntries(prev=>prev.map(e=>
          (e.submission_id||e.id)===submissionId ? {...e, ongoing:false} : e
        ));
      }
    } catch(err){
      console.error("Resolve failed:", err);
    }
  }
  return(
    <div className="fade">
      {showCheckIn&&authSession&&<DailyCheckInModal authSession={authSession} onClose={()=>setShowCheckIn(false)} onSuccess={()=>{setShowCheckIn(false);setCheckInKey(k=>k+1);}}/>}
      {showConfirmation&&postSubmit&&<ConfirmationCard submissionId={postSubmit.submissionId} warriorId={postSubmit.warriorId} onDismiss={()=>setShowConfirmation(false)} allData={localEntries}/>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:10}}>
        <button onClick={onReset} style={{background:"none",border:"none",color:C.muted,fontSize:13,display:"flex",alignItems:"center",gap:6,padding:0}}>← Search again</button>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {isOwnProfile&&(
            <button onClick={()=>setShowCheckIn(true)} style={{background:C.teal,border:"none",borderRadius:8,padding:"7px 16px",color:"#fff",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>📋 Log Today</button>
          )}
          <button onClick={()=>setShowEDMode(true)} style={{background:C.red,border:"none",borderRadius:8,padding:"7px 16px",color:"#fff",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>🏥 Show ED</button>
          <button onClick={()=>setShowProtocolBuilder(true)} style={{background:"none",border:`1px solid ${C.teal}50`,borderRadius:8,padding:"7px 16px",color:C.teal,fontSize:12,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>⚕ Build Protocol</button>
          <button onClick={()=>generateBrief(localEntries)} style={{background:"none",border:`1px solid ${C.amber}50`,borderRadius:8,padding:"7px 16px",color:C.amber,fontSize:12,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>⬇ Export Clinical Brief</button>
        </div>
      </div>
      {isOwnProfile&&<CheckInHistory key={checkInKey} authSession={authSession}/>}
      <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:24,marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:14,marginBottom:20}}>
          <div>
            <div style={{fontFamily:"Syne",fontWeight:800,fontSize:22,marginBottom:2}}>{entries[0].warriorId}</div>
            <div style={{fontSize:13,color:C.muted}}>{entries[0].community} · Tracking for {entries[0].trackingFor}{spanDays>0&&` · ${spanDays} days of data`}</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(90px,1fr))",gap:10,flex:1,maxWidth:420}}>
            <Pill label="Crises Logged" value={localEntries.length} accent={C.amber}/>
            <Pill label="Avg Pain" value={avgPain(localEntries)} accent={C.red}/>
            <Pill label="ER Visits" value={erCount} accent={C.amber}/>
            {violCount>0&&<Pill label="Violations" value={violCount} accent={C.red}/>}
          </div>
        </div>
        <SignalJournal entries={localEntries} authSession={authSession} api={SJ_API} C={C}/>
      </div>
      <div style={{fontSize:11,color:C.muted,textAlign:"center",marginBottom:14}}>
        Your data ·{" "}
        <button onClick={exportMyData} style={{background:"none",border:"none",color:C.teal,fontSize:11,cursor:"pointer",padding:0,textDecoration:"underline"}}>Export CSV</button>
        {" "}·{" "}
        <a href={`mailto:info@kindredcompassholdings.com?subject=Data Request — ${localEntries[0].warriorId}`} style={{color:C.teal,fontSize:11}}>Request deletion</a>
      </div>
      <div style={{fontSize:10,letterSpacing:2.5,color:C.muted,textTransform:"uppercase",marginBottom:14}}>Crisis Timeline — oldest first</div>
      {sorted.map((e,i)=><Entry key={e.id} e={e} isLast={i===sorted.length-1} onResolve={isOwnProfile ? handleResolve : undefined} onLogViolation={handleLogViolation}/>)}
      {showProtocolBuilder&&<ProtocolBuilder entries={localEntries} onClose={()=>setShowProtocolBuilder(false)}/> }
      {showEDMode&&<EDMode entries={localEntries} onClose={()=>setShowEDMode(false)}/>}
    </div>
  );
}

// ── TIMELINE SEARCH ───────────────────────────────────────────────────────────
function TimelineView({postSubmit,allData,authSession,onRequestLogin}){
  const [input,setInput]=useState("");
  const [phase,setPhase]=useState("idle");
  const [results,setResults]=useState(null);
  const [fuzzyOpts,setFuzzyOpts]=useState([]);

  // Signing out clears the open timeline and journal immediately (shared devices).
  useEffect(()=>{
    if(!authSession){ setResults(null); setFuzzyOpts([]); setPhase("idle"); setInput(""); }
  },[authSession]);

  // Auto-load from auth session — signed-in warrior goes straight to their profile
  useEffect(()=>{
    if(authSession&&authSession.warriorId&&allData&&allData.length){
      setInput(authSession.warriorId);
      const{exact,fuzzy}=findMatches(authSession.warriorId);
      if(exact.length){ setResults(exact.flatMap(m=>m.entries)); setPhase("found"); }
      else if(fuzzy.length){ setFuzzyOpts(fuzzy); setPhase("confirm"); }
      else { setPhase("notfound"); } // signed in but no submissions yet
    }
  },[authSession,allData]);

  // Re-run auto-search when postSubmit arrives AND data is loaded
  useEffect(()=>{
    if(postSubmit&&postSubmit.warriorId&&allData&&allData.length){
      setInput(postSubmit.warriorId);
      const{exact,fuzzy}=findMatches(postSubmit.warriorId);
      if(exact.length){
        setResults(exact.flatMap(m=>m.entries));
        setPhase("found");
      }else if(fuzzy.length){
        setFuzzyOpts(fuzzy);
        setPhase("confirm");
      }else{
        setPhase("newsubmission");
      }
    }
  },[postSubmit,allData]);

  function search(){
    if(!input.trim())return;
    const{exact,fuzzy}=findMatches(input.trim());
    if(exact.length){setResults(exact.flatMap(m=>m.entries));setPhase("found");}
    else if(fuzzy.length){setFuzzyOpts(fuzzy);setPhase("confirm");}
    else setPhase("notfound");
  }
  function reset(){setInput("");setPhase("idle");setResults(null);setFuzzyOpts([]);}

  if(phase==="found"&&results)return<WarriorProfile entries={results} onReset={reset} postSubmit={postSubmit} allData={allData} authSession={authSession}/>;

  if(phase==="newsubmission"&&postSubmit)return(
    <div className="fade">
      <ConfirmationCard submissionId={postSubmit.submissionId} warriorId={postSubmit.warriorId} onDismiss={reset} allData={allData}/>
      <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:12,padding:20,textAlign:"center"}}>
        <div style={{fontSize:14,color:C.muted,lineHeight:1.7}}>Your data will appear here once it's processed — usually within a few minutes.<br/>Come back and search your Warrior ID to see your timeline.</div>
      </div>
    </div>
  );

  return(
    <div>
      <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:16,padding:32,marginBottom:20}}>
        <div style={{fontFamily:"Syne",fontWeight:800,fontSize:24,marginBottom:6,letterSpacing:-.3}}>Your Crisis <span style={{color:C.amber}}>Timeline</span></div>
        <div style={{fontSize:14,color:C.muted,marginBottom:26,lineHeight:1.7,maxWidth:560}}>Every crisis you've logged — your triggers, your ER encounters, your patterns — in one place. Enter your Warrior ID to access your data.</div>

        {/* Secure login prompt — shown only when not authenticated */}
        {!authSession&&(
          <div style={{background:`${C.teal}0a`,border:`1px solid ${C.teal}25`,borderRadius:12,padding:16,marginBottom:20,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:C.teal,marginBottom:3}}>🔐 Secure Access Available</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>Log in with your phone number to go straight to your timeline — no ID needed.</div>
            </div>
            <button onClick={onRequestLogin} style={{background:C.teal,color:"#fff",border:"none",borderRadius:8,padding:"9px 20px",fontSize:13,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>Login with Phone</button>
          </div>
        )}

        <div style={{display:"flex",gap:10,marginBottom:14}}>
          <input value={input} onChange={e=>{setInput(e.target.value);if(phase!=="idle")setPhase("idle");}} onKeyDown={e=>e.key==="Enter"&&search()} placeholder="Enter your Warrior ID" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" style={{flex:1,background:C.surf2,border:`1px solid ${phase==="notfound"?C.red+"50":C.border}`,borderRadius:10,padding:"13px 18px",color:C.text,fontSize:14,transition:"border .2s"}}/>
          <button onClick={search} style={{background:C.amber,color:"#fff",border:"none",borderRadius:10,padding:"13px 28px",fontWeight:500,fontSize:14}}>Find Me</button>
        </div>
        <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>Make up a Warrior ID only you would recognize. Don’t use your real name. Your data is yours.</div>
      </div>
      {phase==="confirm"&&<div className="fade" style={{background:C.surf,border:`1px solid ${C.amber}30`,borderRadius:14,padding:24,marginBottom:16}}><div style={{fontFamily:"Syne",fontWeight:700,color:C.amber,marginBottom:4}}>Did you mean one of these?</div><div style={{fontSize:13,color:C.muted,marginBottom:16}}>We couldn't find an exact match for "<strong style={{color:C.text}}>{input}</strong>" but found similar IDs:</div><div style={{display:"flex",flexDirection:"column",gap:8}}>{fuzzyOpts.map(opt=>(<button key={opt.warriorId} onClick={()=>{setResults([...opt.entries]);setPhase("found");}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.surf2,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 18px",color:C.text,textAlign:"left"}}><div><div style={{fontWeight:500,fontSize:15,marginBottom:2}}>{opt.warriorId}</div><div style={{fontSize:12,color:C.muted}}>{opt.entries[0].community} · {opt.entries.length} submission{opt.entries.length>1?"s":""}</div></div><span style={{color:C.amber,fontSize:13}}>This is me →</span></button>))}</div><button onClick={()=>setPhase("idle")} style={{background:"none",border:"none",color:C.muted,fontSize:12,marginTop:12,padding:0}}>← None of these are me</button></div>}
      {phase==="notfound"&&(
        <div className="fade" style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:28,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:10}}>🔍</div>
          <div style={{fontFamily:"Syne",fontWeight:700,fontSize:17,marginBottom:8}}>No match for "{input}"</div>
          <div style={{fontSize:13,color:C.muted,lineHeight:1.7,maxWidth:420,margin:"0 auto"}}>
            {authSession
              ? <>
                  You're logged in, but no submissions matched. The system searched for: <strong style={{color:C.amber,fontFamily:"monospace"}}>{input||authSession.warriorId||"(no ID linked)"}</strong>
                  <br/><br/>
                  Your Warrior ID in the database may be capitalized or formatted differently. Try typing it exactly as you entered it on the submission form — or check the Supabase <code style={{color:C.teal,fontSize:11}}>public_crisis_data</code> table to see how it's stored.
                </>
              : <>Double-check the Warrior ID you made up.<br/>If you haven't submitted yet, your ID won't be in the system.</>
            }
          </div>
          <button onClick={reset} style={{marginTop:16,background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 18px",color:C.muted,fontSize:12,cursor:"pointer"}}>← Try a different ID</button>
        </div>
      )}
    </div>
  );
}

// ── COMMUNITY VIEW ────────────────────────────────────────────────────────────
function CommunityView(){
  const triggers=countT(COMMUNITY_RAW);
  const maxT=triggers[0]?.[1]||1;
  const erVisits=COMMUNITY_RAW.filter(d=>d.erVisit==="Yes");
  const violations=allViolations();
  const menstrual=COMMUNITY_RAW.filter(d=>d.triggered.some(t=>t.includes("Menstrual")));
  const consented=COMMUNITY_RAW.filter(d=>d.consented);
  const withAge=COMMUNITY_RAW.filter(d=>d.age);
  return(
    <div className="fade">
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:24}}>
        <Pill label="Total Reports" value={COMMUNITY_RAW.length} accent={C.blue} sub="Dec 2025 – present" tooltip={`${COMMUNITY_RAW.length} crisis reports submitted Dec 2025 through present. Updates live as new submissions arrive.`}/>
        <Pill label="Avg Pain" value={avgPain(COMMUNITY_RAW)} accent={C.red} sub="Across all crises" tooltip={`Average pain level (0–10 scale) across all ${COMMUNITY_RAW.length} reports. Excludes entries logged at 0.`}/>
        <Pill label="ER Visits" value={erVisits.length} accent={C.amber} tooltip={`${erVisits.length} of ${COMMUNITY_RAW.length} crisis reports involved an ER visit. Dec 2025 – present.`}/>
        <Pill label="Protocol Violations" value={erVisits.length>0?`${Math.round(violations.length/erVisits.length*100)}%`:"—"} accent={C.red} sub={`${violations.length} of ${erVisits.length} ER visits`} tooltip={`Protocol not followed in ${violations.length} of ${erVisits.length} ER visits. Self-reported by Warriors at point of care. Dec 2025 – present.`}/>
        <Pill label="Menstrual Trigger" value={`${Math.round(menstrual.length/COMMUNITY_RAW.length*100)}%`} accent={C.purple} sub={`${menstrual.length} reports`} tooltip={`${menstrual.length} of ${COMMUNITY_RAW.length} reports cite menstrual cycle as a documented crisis trigger. Pattern surfaced by community data.`}/>
        <Pill label="Warrior Data Sovereignty" value={consented.length} accent={C.teal} tooltip={`${consented.length} submissions include explicit consent to share with the community.`}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:22}}>
          <div style={{fontFamily:"Syne",fontWeight:700,marginBottom:3}}>Crisis Triggers</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:18}}>Reported 24–48 hrs before onset</div>
          {triggers.map(([label,count])=>{
            const pct=Math.round(count/COMMUNITY_RAW.length*100);
            const isMen=label==="Menstrual Cycle";
            const isThermal=label==="THERMAL SHOCK";
            return(<div key={label} style={{marginBottom:11}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:isThermal?1:3}}><span style={{fontSize:13,color:isMen?C.purple:isThermal?C.blue:C.text}}>{label}{isMen&&<span style={{marginLeft:6,fontSize:10,background:`${C.purple}18`,color:C.purple,padding:"1px 6px",borderRadius:4}}>COMMUNITY IDENTIFIED</span>}</span><span style={{fontSize:12,color:C.muted}}>{count} ({pct}%)</span></div>{isThermal&&<div style={{fontSize:10,color:C.muted,marginBottom:4,lineHeight:1.4,fontStyle:"italic"}}>Rapid temperature changes, cold exposure, air conditioning, wind chill, or sudden environmental transitions</div>}<div style={{height:5,background:"rgba(255,255,255,0.05)",borderRadius:3}}><div style={{height:"100%",width:`${Math.round(count/maxT*100)}%`,background:isMen?C.purple:isThermal?C.blue:C.blue,borderRadius:3}}/></div></div>);
          })}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:`${C.red}0a`,border:`1px solid ${C.red}25`,borderRadius:14,padding:20,flex:1}}>
            <div style={{fontFamily:"Syne",fontWeight:700,color:C.red,marginBottom:6,fontSize:14}}>Systemic Pattern: Addict Stigma</div>
            <div style={{fontSize:13,lineHeight:1.75,color:"rgba(255,255,255,0.7)"}}>{Math.round(violations.length/erVisits.length*100)}% of ER visits ended in protocol violations. Warriors are denied admission despite documented pain protocols — clinical teams citing addiction risk over lived experience.</div>
            <div style={{marginTop:12,fontSize:12,color:C.red,fontStyle:"italic",borderLeft:`2px solid ${C.red}40`,paddingLeft:10}}>"I hate being treated as an addict rather than a patient."</div>
          </div>
          <div style={{background:`${C.purple}0a`,border:`1px solid ${C.purple}25`,borderRadius:14,padding:20,flex:1}}>
            <div style={{fontFamily:"Syne",fontWeight:700,color:C.purple,marginBottom:6,fontSize:14}}>Community-Identified: Menstrual Trigger</div>
            <div style={{fontSize:13,lineHeight:1.75,color:"rgba(255,255,255,0.7)"}}>{menstrual.length} Warriors ({Math.round(menstrual.length/COMMUNITY_RAW.length*100)}%) cite menstrual cycle as a trigger in their consented entries.</div>
          </div>
          {consented.length>0&&<div style={{background:`${C.teal}0a`,border:`1px solid ${C.teal}25`,borderRadius:14,padding:20}}>
            <div style={{fontFamily:"Syne",fontWeight:700,color:C.teal,marginBottom:6,fontSize:14}}>Warrior Data Sovereignty</div>
            <div style={{fontSize:13,lineHeight:1.75,color:"rgba(255,255,255,0.7)"}}>All submissions collected under informed consent at point of entry. {consented.length} submissions include explicit database-level consent confirmation.</div>
          </div>}
        </div>
      </div>
    </div>
  );
}

// ── ACCOUNTABILITY VIEW ───────────────────────────────────────────────────────
function AccountabilityView(){
  const violations=allViolations();
  const erVisits=COMMUNITY_RAW.filter(d=>d.erVisit==="Yes");
  // Wait times are mixed numeric/string (Supabase returns text like "31-60 mins") — normalize to hours
  const toHours=w=>{
    if(!w||w===0||w==="0")return null;
    if(typeof w==="number"&&w>0&&w<48)return w;
    if(typeof w==="string"){
      const l=w.toLowerCase();
      if(l.includes("0-30")||l.includes("< 30"))return 0.25;
      if(l.includes("30-60")||l.includes("31-60"))return 0.75;
      if(l.includes("1-2 hour"))return 1.5;
      if(l.includes("2-4 hour"))return 3;
      if(l.includes("4-8 hour"))return 6;
      if(l.includes("4+")||l.includes("> 4"))return 5;
      const n=parseFloat(w);
      if(!isNaN(n)&&n>0&&n<48)return n;
    }
    return null;
  };
  const waitHrs=erVisits.map(d=>toHours(d.waitHours)).filter(n=>n!==null);
  const sortedW=[...waitHrs].sort((a,b)=>a-b);
  const medianWait=sortedW.length?sortedW[Math.floor(sortedW.length/2)]:null;
  const fmtH=h=>h===null?"—":h<1?`${Math.round(h*60)} min`:`${h%1===0?h:h.toFixed(1)} hr`;
  return(
    <div className="fade">
      <div style={{background:`${C.red}08`,border:`1px solid ${C.red}20`,borderRadius:14,padding:20,marginBottom:20}}>
        <div style={{fontFamily:"Syne",fontWeight:700,color:C.red,marginBottom:6}}>Community-Owned Evidence</div>
        <div style={{fontSize:13,lineHeight:1.75,color:"rgba(255,255,255,0.7)"}}>These records document systemic patterns, not individual accusations. Protocol deviations represent design failures in how healthcare systems treat SCD patients. This data belongs to Warriors for advocacy, hospital quality improvement conversations, and policy reform.</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}}>
        <Pill label="ER Visits" value={erVisits.length} accent={C.blue}/>
        <Pill label="Violation Rate" value={`${Math.round(violations.length/erVisits.length*100)}%`} accent={C.red} sub={`${violations.length} incidents`}/>
        <Pill label="Median Wait" value={fmtH(medianWait)} accent={C.amber} sub={waitHrs.length?`${waitHrs.length} recorded`:"none recorded"}/>
      </div>
      <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:22}}>
        <div style={{fontFamily:"Syne",fontWeight:700,marginBottom:3}}>Documented Protocol Violations</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:18}}>{violations.length} incidents on record · Community-owned · For quality improvement conversations only</div>
        {violations.map(v=>(
          <div key={v.id} style={{padding:"14px 0",borderBottom:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,flexWrap:"wrap",gap:4}}>
              <span style={{fontWeight:500,fontSize:14}}>Warrior — {v.community}</span>
              <span style={{fontSize:11,color:C.muted}}>{v.submitted}</span>
            </div>
            <div style={{fontSize:13,marginBottom:4}}><span style={{color:C.red}}>🏥 {v.hospital||"Unknown"} </span><span style={{color:C.muted}}>· {fmtWait(v.waitHours)||"wait not recorded"} · Pain {v.pain}/10</span></div>
            {v.whyNot&&<div style={{fontSize:12,color:C.muted,fontStyle:"italic"}}>Reason given: {v.whyNot}</div>}
            {v.notes&&<div style={{marginTop:6,fontSize:12,fontStyle:"italic",color:C.amber,borderLeft:`2px solid ${C.amber}40`,paddingLeft:10}}>"{v.notes}"</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CONFIRMATION CARD ─────────────────────────────────────────────────────────
function ConfirmationCard({submissionId,warriorId,onDismiss,allData}){
  // Compute stats from already-loaded data — no separate Supabase call needed
  const stats=allData&&allData.length?( ()=>{
    const uniqueWarriors=new Set(allData.map(r=>r.warriorId).filter(Boolean));
    const now=new Date().toISOString().slice(0,10);
    const todayCount=allData.filter(r=>r.submitted===now).length;
    return{warriorCount:uniqueWarriors.size,todayCount};
  })():null;

  const briefId=`HII-${warriorId}-${new Date().toISOString().slice(0,10)}-${Math.floor(Math.random()*1000).toString().padStart(3,'0')}`;

  return(
    <div className="fade" style={{background:C.surf,border:`2px solid ${C.teal}50`,borderRadius:14,padding:24,marginBottom:24,background:`linear-gradient(135deg,${C.surf} 0%,${C.surf2} 100%)`}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <span style={{fontSize:24,color:C.green}}>✓</span>
            <div style={{fontFamily:"Syne",fontWeight:700,fontSize:18,color:C.text}}>Crisis Report Logged</div>
          </div>
          <div style={{fontSize:13,color:C.muted,marginBottom:14}}>Your submission has been received and assigned an Intelligence Brief ID for tracking.</div>
          <div style={{background:C.surf2,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:20}}>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Brief ID</div>
            <div style={{fontFamily:"monospace",fontSize:14,color:C.teal,fontWeight:600}}>{briefId}</div>
          </div>
        </div>
        <button onClick={onDismiss} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",padding:0,lineHeight:1}}>✕</button>
      </div>
      <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16}}>
        <div style={{fontFamily:"Syne",fontWeight:700,fontSize:14,marginBottom:12,color:C.text}}>🔗 Your Data is Working</div>
        {stats?(<div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:14}}>
          <div style={{background:C.surf,borderRadius:8,padding:12,textAlign:"center"}}>
            <div style={{fontFamily:"Syne",fontWeight:700,fontSize:16,color:C.blue}}>{stats.warriorCount}+</div>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Warriors Active</div>
          </div>
          <div style={{background:C.surf,borderRadius:8,padding:12,textAlign:"center"}}>
            <div style={{fontFamily:"Syne",fontWeight:700,fontSize:16,color:C.amber}}>{stats.todayCount}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Today's Reports</div>
          </div>
        </div>):null}
        <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>Right now, your crisis is being added to the community intelligence record. Warriors like you are revealing systemic patterns that clinics and hospitals can no longer ignore.</div>
      </div>
      <div style={{marginTop:16,display:"flex",gap:10}}>
        <button onClick={onDismiss} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 16px",color:C.muted,fontSize:12,fontWeight:500,cursor:"pointer",transition:"all .2s"}} onMouseEnter={e=>e.target.style.background=C.surf} onMouseLeave={e=>e.target.style.background="none"}>Dismiss</button>
      </div>
    </div>
  );
}

// ── DAILY CHECK-IN ────────────────────────────────────────────────────────────
async function submitCheckIn(authSession, form) {
  const today = new Date().toISOString().slice(0, 10);
  const payload = {
    warrior_uuid: authSession.userId,
    warrior_id: authSession.warriorId,
    check_in_date: today,
    pain_level: form.pain,
    hydration_level: form.hydration,
    sleep_quality: form.sleep,
    sleep_hours: form.sleepHours || null,
    stress_level: form.stress,
    meds_taken: form.meds,
    temp_exposure: form.temp,
    exertion_level: form.exertion,
    notes: form.notes || null,
    source: "manual",
  };
  const res = await fetch(`${SUPABASE_URL}/rest/v1/daily_checkins`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${authSession.accessToken}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || e.msg || "Failed to save check-in.");
  }
  return true;
}

async function fetchRecentCheckIns(authSession) {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/daily_checkins?warrior_uuid=eq.${authSession.userId}&order=check_in_date.desc&limit=14`,
    {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${authSession.accessToken}`,
      },
    }
  );
  if (!res.ok) return [];
  return res.json();
}

// ── RESOLVE CRISIS ────────────────────────────────────────────────────────────
// Marks a crisis entry as resolved (ongoing → false) via Supabase PATCH.
// Requires the warrior's own access token — RLS will reject anyone else.
async function resolveCrisis(submissionId, accessToken) {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/public_crisis_data?submission_id=eq.${encodeURIComponent(submissionId)}`,
    {
      method: "PATCH",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken || SUPABASE_ANON_KEY}`,
        "Content-Type": "application/json",
        Prefer: "return=minimal",
      },
      body: JSON.stringify({ ongoing: false }),
    }
  );
  if (res.status === 204 || res.ok) return { success: true };
  if (res.status === 409) return { alreadyResolved: true };
  const err = await res.json().catch(() => ({}));
  throw new Error(err.message || err.msg || `Resolve failed (${res.status})`);
}

function DailyCheckInModal({ authSession, onClose, onSuccess }) {
  const [form, setForm] = useState({
    pain: 0, hydration: "", sleep: "", sleepHours: "", stress: 0,
    meds: "", temp: "", exertion: "", notes: "",
  });
  const [step, setStep] = useState(1); // 1 = quick, 2 = detail, 3 = done
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit() {
    if (!form.hydration || !form.sleep || !form.meds) {
      setErr("Please answer all required questions before submitting."); return;
    }
    setBusy(true); setErr("");
    try {
      await submitCheckIn(authSession, form);
      setStep(3);
      setTimeout(() => { onSuccess(); onClose(); }, 2200);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const overlay = { position:"fixed",inset:0,background:"rgba(0,0,0,0.80)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20,backdropFilter:"blur(4px)" };
  const card = { background:C.surf,border:`1px solid ${C.border}`,borderRadius:18,padding:28,width:"100%",maxWidth:460,position:"relative",boxShadow:"0 24px 64px rgba(0,0,0,0.6)",maxHeight:"90vh",overflowY:"auto" };
  const sec = { marginBottom:22 };
  const lbl = { fontSize:12,color:C.muted,marginBottom:8,display:"block",fontWeight:500 };
  const chipRow = { display:"flex",gap:8,flexWrap:"wrap" };
  const chip = (active, color) => ({
    padding:"7px 14px", borderRadius:20, fontSize:13, fontWeight:500, cursor:"pointer",
    background: active ? `${color}20` : C.surf2,
    border: `1px solid ${active ? color : C.border}`,
    color: active ? color : C.muted, transition:"all .15s",
  });
  const painColors = ["#22c55e","#4ade80","#86efac","#fbbf24","#fb923c","#f97316","#ef4444","#dc2626","#b91c1c","#991b1b","#7f1d1d"];

  if (step === 3) return (
    <div style={overlay}>
      <div style={{ ...card, textAlign:"center", padding:40 }} className="fade">
        <div style={{ fontSize:52, marginBottom:16 }}>✓</div>
        <div style={{ fontFamily:"Syne", fontWeight:800, fontSize:20, marginBottom:8 }}>Check-In Logged</div>
        <div style={{ fontSize:13, color:C.muted, lineHeight:1.7 }}>Your daily data is building the picture. Every entry counts.</div>
      </div>
    </div>
  );

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card} className="fade">
        <button onClick={onClose} style={{ position:"absolute",top:14,right:16,background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer",lineHeight:1 }}>✕</button>

        <div style={{ fontSize:11,letterSpacing:3,color:C.muted,textTransform:"uppercase",marginBottom:6 }}>Daily Check-In</div>
        <div style={{ fontFamily:"Syne",fontWeight:800,fontSize:20,marginBottom:4 }}>How are you <span style={{ color:C.teal }}>today?</span></div>
        <div style={{ fontSize:12,color:C.muted,marginBottom:24,lineHeight:1.6 }}>Under 60 seconds. Your entries help you track patterns in your own care.</div>

        {/* Pain */}
        <div style={sec}>
          <label style={lbl}>Pain level right now <span style={{ color:C.red }}>*</span></label>
          <div style={{ display:"flex",gap:4,alignItems:"center",flexWrap:"wrap" }}>
            {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => set("pain", n)} style={{
                width:34,height:34,borderRadius:"50%",border:"none",cursor:"pointer",fontWeight:700,fontSize:12,
                background: form.pain === n ? painColors[n] : C.surf2,
                color: form.pain === n ? "#fff" : C.muted,
                transform: form.pain === n ? "scale(1.15)" : "scale(1)", transition:"all .15s",
              }}>{n}</button>
            ))}
          </div>
          <div style={{ fontSize:11,color:C.muted,marginTop:6 }}>
            {form.pain === 0 ? "No pain" : form.pain <= 3 ? "Mild" : form.pain <= 6 ? "Moderate" : form.pain <= 8 ? "Severe" : "Crisis-level"}
          </div>
        </div>

        {/* Hydration */}
        <div style={sec}>
          <label style={lbl}>Hydration today <span style={{ color:C.red }}>*</span></label>
          <div style={chipRow}>
            {[["💧 Less than usual","low",C.red],["✓ About right","normal",C.teal],["💧💧 More than usual","high",C.blue]].map(([label,val,color]) => (
              <button key={val} onClick={() => set("hydration", val)} style={chip(form.hydration === val, color)}>{label}</button>
            ))}
          </div>
        </div>

        {/* Sleep */}
        <div style={sec}>
          <label style={lbl}>Sleep last night <span style={{ color:C.red }}>*</span></label>
          <div style={chipRow}>
            {[["😴 Good","good",C.teal],["😐 Okay","okay",C.amber],["😞 Poor","poor",C.red]].map(([label,val,color]) => (
              <button key={val} onClick={() => set("sleep", val)} style={chip(form.sleep === val, color)}>{label}</button>
            ))}
          </div>
        </div>

        {/* Stress */}
        <div style={sec}>
          <label style={lbl}>Stress level today</label>
          <div style={{ display:"flex",gap:6 }}>
            {[["😌 Calm",1,C.teal],["🙂 Mild",2,C.blue],["😤 Moderate",3,C.amber],["😰 High",4,"#f97316"],["🔥 Overwhelmed",5,C.red]].map(([label,val,color]) => (
              <button key={val} onClick={() => set("stress", val)} style={{ ...chip(form.stress === val, color), flex:1, fontSize:11, padding:"8px 4px", textAlign:"center" }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Meds */}
        <div style={sec}>
          <label style={lbl}>Medications today <span style={{ color:C.red }}>*</span></label>
          <div style={chipRow}>
            {[["✓ Took all","yes",C.teal],["~ Partly","partly",C.amber],["✗ Missed","no",C.red]].map(([label,val,color]) => (
              <button key={val} onClick={() => set("meds", val)} style={chip(form.meds === val, color)}>{label}</button>
            ))}
          </div>
        </div>

        {/* Temp + Exertion */}
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:22 }}>
          <div>
            <label style={lbl}>Temperature exposure</label>
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              {[["❄️ Cold","cold",C.blue],["☀️ Normal","normal",C.teal],["🔥 Hot/humid","hot",C.red]].map(([label,val,color]) => (
                <button key={val} onClick={() => set("temp", val)} style={chip(form.temp === val, color)}>{label}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Physical exertion</label>
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              {[["🛋️ Rest","low",C.teal],["🚶 Moderate","moderate",C.amber],["🏃 Heavy","high",C.red]].map(([label,val,color]) => (
                <button key={val} onClick={() => set("exertion", val)} style={chip(form.exertion === val, color)}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={sec}>
          <label style={lbl}>Anything else? <span style={{ color:C.muted,fontWeight:400 }}>(optional)</span></label>
          <textarea
            value={form.notes}
            onChange={e => set("notes", e.target.value)}
            placeholder="How you're feeling, anything different today, what you noticed..."
            rows={2}
            style={{ width:"100%",boxSizing:"border-box",background:C.surf2,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 14px",color:C.text,fontSize:13,resize:"vertical",fontFamily:"inherit",outline:"none" }}
          />
        </div>

        {err && <div style={{ fontSize:12,color:C.red,marginBottom:12,lineHeight:1.5 }}>{err}</div>}

        <button
          onClick={handleSubmit}
          disabled={busy}
          style={{ width:"100%",background:C.teal,color:"#fff",border:"none",borderRadius:10,padding:"13px 0",fontWeight:700,fontSize:14,cursor:busy?"not-allowed":"pointer",opacity:busy?0.6:1 }}
        >
          {busy ? "Saving…" : "Log Today's Check-In"}
        </button>

        <div style={{ fontSize:11,color:C.muted,marginTop:12,textAlign:"center",lineHeight:1.6 }}>
          Your check-ins are private to your account and are not included in community insights. Authorized administrators may access stored data only for security, safety, and support purposes.
        </div>
      </div>
    </div>
  );
}

function CheckInHistory({ authSession }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    if (!authSession) return;
    fetchRecentCheckIns(authSession).then(setHistory).catch(() => setHistory([]));
  }, [authSession]);

  if (!history || !history.length) return null;

  const streak = (() => {
    const today = new Date().toISOString().slice(0, 10);
    let count = 0;
    const sorted = [...history].sort((a, b) => b.check_in_date.localeCompare(a.check_in_date));
    for (let i = 0; i < sorted.length; i++) {
      const expected = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      if (sorted[i].check_in_date === expected) count++;
      else break;
    }
    return count;
  })();

  const painColor = p => p <= 3 ? C.teal : p <= 6 ? C.amber : C.red;

  return (
    <div style={{ background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:20 }} className="fade">
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
        <div>
          <div style={{ fontFamily:"Syne",fontWeight:700,fontSize:15,marginBottom:2 }}>Daily Check-In History</div>
          <div style={{ fontSize:12,color:C.muted }}>Last {history.length} entries</div>
        </div>
        {streak > 1 && (
          <div style={{ textAlign:"center",background:`${C.amber}15`,border:`1px solid ${C.amber}30`,borderRadius:10,padding:"8px 14px" }}>
            <div style={{ fontSize:20,lineHeight:1 }}>🔥</div>
            <div style={{ fontSize:11,color:C.amber,fontWeight:700,marginTop:2 }}>{streak}-day streak</div>
          </div>
        )}
      </div>
      <div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>
        {[...history].sort((a,b) => a.check_in_date.localeCompare(b.check_in_date)).map(h => (
          <div key={h.id} title={`${h.check_in_date} · Pain ${h.pain_level ?? "—"} · ${h.sleep_quality ?? "—"} sleep · Hydration: ${h.hydration_level ?? "—"}`}
            style={{ width:28,height:28,borderRadius:6,background:`${painColor(h.pain_level ?? 0)}25`,border:`2px solid ${painColor(h.pain_level ?? 0)}60`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:painColor(h.pain_level ?? 0),cursor:"default" }}>
            {h.pain_level ?? "?"}
          </div>
        ))}
      </div>
      <div style={{ fontSize:10,color:C.muted,marginTop:10 }}>Each box = one day. Color = pain level. Hover for details.</div>
    </div>
  );
}

// ── PHONE AUTH MODAL ─────────────────────────────────────────────────────────
function PhoneAuthModal({ onSuccess, onClose }) {
  const [phase, setPhase] = useState("phone"); // phone | otp | link | done
  const [phone, setPhone] = useState("+1");
  const [otp, setOtp] = useState("");
  const [linkId, setLinkId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [supaSession, setSupaSession] = useState(null);

  async function handleSendOTP() {
    const num = phone.trim().replace(/\s/g, "");
    if (!/^\+\d{10,15}$/.test(num)) { setErr("Enter a valid number with country code, e.g. +17165550123"); return; }
    setBusy(true); setErr("");
    try { await authSendOTP(num); setPhase("otp"); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleVerifyOTP() {
    if (!/^\d{6}$/.test(otp)) { setErr("Enter the 6-digit code from your text message"); return; }
    setBusy(true); setErr("");
    try {
      const data = await authVerifyOTP(phone.trim().replace(/\s/g, ""), otp);
      setSupaSession(data);
      const existingId = await fetchLinkedWarriorId(data.access_token, data.user.id);
      if (existingId) {
        saveSession({ accessToken: data.access_token, userId: data.user.id, warriorId: existingId, phone: phone.trim() });
        onSuccess({ warriorId: existingId, accessToken: data.access_token, userId: data.user.id, phone: phone.trim() });
        setPhase("done");
      } else {
        setPhase("link");
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleLinkId() {
    const wid = linkId.trim();
    if (!wid) { setErr("Enter your Warrior ID to continue"); return; }
    if (!supaSession) { setErr("Session expired — please start over."); return; }
    setBusy(true); setErr("");
    try {
      await saveWarriorLink(supaSession.access_token, supaSession.user.id, wid, phone.trim());
      saveSession({ accessToken: supaSession.access_token, userId: supaSession.user.id, warriorId: wid, phone: phone.trim() });
      onSuccess({ warriorId: wid, accessToken: supaSession.access_token, userId: supaSession.user.id, phone: phone.trim() });
      setPhase("done");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  if (phase === "done") return null;

  const overlay = { position:"fixed",inset:0,background:"rgba(0,0,0,0.78)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20,backdropFilter:"blur(4px)" };
  const card = { background:C.surf,border:`1px solid ${C.border}`,borderRadius:18,padding:32,width:"100%",maxWidth:420,position:"relative",boxShadow:"0 24px 64px rgba(0,0,0,0.6)" };
  const inp = { width:"100%",boxSizing:"border-box",background:C.surf2,border:`1px solid ${C.border}`,borderRadius:10,padding:"13px 16px",color:C.text,fontSize:15,outline:"none",marginBottom:10,fontFamily:"inherit" };
  const primaryBtn = { width:"100%",background:C.red,color:"#fff",border:"none",borderRadius:10,padding:"13px 0",fontWeight:700,fontSize:14,cursor:busy?"not-allowed":"pointer",opacity:busy?0.55:1,transition:"opacity .2s",fontFamily:"inherit" };
  const ghostBtn = { width:"100%",background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 0",color:C.muted,fontSize:13,cursor:"pointer",marginTop:8,fontFamily:"inherit" };
  const linkBtn = { background:"none",border:"none",color:C.muted,fontSize:12,cursor:"pointer",padding:"8px 0",textDecoration:"underline",fontFamily:"inherit" };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card} className="fade">
        <button onClick={onClose} style={{ position:"absolute",top:14,right:16,background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer",lineHeight:1,fontWeight:300 }}>✕</button>

        {phase === "phone" && (
          <>
            <div style={{ fontSize:11,letterSpacing:3,color:C.muted,textTransform:"uppercase",marginBottom:8 }}>Secure Access</div>
            <div style={{ fontFamily:"Syne",fontWeight:800,fontSize:22,marginBottom:6 }}>Access Your <span style={{ color:C.amber }}>Timeline</span></div>
            <div style={{ fontSize:13,color:C.muted,marginBottom:26,lineHeight:1.75 }}>We'll text you a one-time code — no passwords, no accounts. Your number is never shared or used for marketing.</div>
            <label style={{ fontSize:12,color:C.muted,display:"block",marginBottom:6 }}>Phone number (include country code)</label>
            <input style={inp} value={phone} onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSendOTP()} placeholder="+17165550123" type="tel" autoComplete="tel" />
            {err && <div style={{ fontSize:12,color:C.red,marginBottom:10,lineHeight:1.5 }}>{err}</div>}
            <button style={primaryBtn} onClick={handleSendOTP} disabled={busy}>{busy ? "Sending…" : "Send Code"}</button>
          </>
        )}

        {phase === "otp" && (
          <>
            <div style={{ fontSize:11,letterSpacing:3,color:C.muted,textTransform:"uppercase",marginBottom:8 }}>Verify Identity</div>
            <div style={{ fontFamily:"Syne",fontWeight:800,fontSize:22,marginBottom:6 }}>Check Your <span style={{ color:C.teal }}>Messages</span></div>
            <div style={{ fontSize:13,color:C.muted,marginBottom:26,lineHeight:1.75 }}>A 6-digit code was sent to <strong style={{ color:C.text }}>{phone}</strong>. Codes expire in 10 minutes.</div>
            <label style={{ fontSize:12,color:C.muted,display:"block",marginBottom:6 }}>Verification code</label>
            <input
              style={{ ...inp, letterSpacing:10, textAlign:"center", fontSize:22, fontFamily:"monospace", paddingLeft:0, paddingRight:0 }}
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={e => e.key === "Enter" && otp.length === 6 && handleVerifyOTP()}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              autoFocus
            />
            {err && <div style={{ fontSize:12,color:C.red,marginBottom:10,lineHeight:1.5 }}>{err}</div>}
            <button style={primaryBtn} onClick={handleVerifyOTP} disabled={busy || otp.length < 6}>{busy ? "Verifying…" : "Verify Code"}</button>
            <div style={{ display:"flex",justifyContent:"space-between",marginTop:12 }}>
              <button style={linkBtn} onClick={() => { setPhase("phone"); setOtp(""); setErr(""); }}>← Change number</button>
              <button style={linkBtn} onClick={() => { setOtp(""); handleSendOTP(); }}>Resend code</button>
            </div>
          </>
        )}

        {phase === "link" && (
          <>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:16 }}>
              <span style={{ fontSize:18,color:C.green }}>✓</span>
              <span style={{ fontSize:13,color:C.muted }}>Phone verified</span>
            </div>
            <div style={{ fontFamily:"Syne",fontWeight:800,fontSize:22,marginBottom:6 }}>Link Your <span style={{ color:C.amber }}>Warrior ID</span></div>
            <div style={{ fontSize:13,color:C.muted,marginBottom:26,lineHeight:1.75 }}>Connect your secure login to your existing crisis history. Your Warrior ID is the one you entered on the tracking form (e.g. <span style={{ color:C.text,fontFamily:"monospace" }}>Jason04730</span>).</div>
            <label style={{ fontSize:12,color:C.muted,display:"block",marginBottom:6 }}>Your Warrior ID</label>
            <input style={inp} value={linkId} onChange={e => setLinkId(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLinkId()} placeholder="e.g. Jason04730" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
            {err && <div style={{ fontSize:12,color:C.red,marginBottom:10,lineHeight:1.5 }}>{err}</div>}
            <button style={primaryBtn} onClick={handleLinkId} disabled={busy || !linkId.trim()}>{busy ? "Linking…" : "Link My Data"}</button>
            <button style={ghostBtn} onClick={onClose}>Skip — I'll link later</button>
            <div style={{ fontSize:11,color:C.muted,marginTop:14,lineHeight:1.6,textAlign:"center" }}>Haven't submitted yet? Skip for now and link after your first submission.</div>
          </>
        )}
      </div>
    </div>
  );
}

// ── ROOT ──────────────────────────────────────────────────────────────────────

// ── INFERENCE FLAGS VIEW ──────────────────────────────────────────────────────
function InferenceFlagsView({authSession}){
  const [flags,setFlags]=useState(null);
  const [err,setErr]=useState(null);
  const accessToken=authSession?.accessToken;

  // Flags are readable only with a signed-in Warrior's token; RLS limits them
  // to flags on that Warrior's own submissions.
  useEffect(()=>{
    if(!accessToken) return;
    fetch(`${SUPABASE_URL}/rest/v1/inference_flags?select=*&order=created_at.asc`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${accessToken}`}
    })
    .then(r=>r.ok?r.json():Promise.reject(r.status))
    .then(setFlags)
    .catch(e=>setErr(`Could not load intelligence data (${e})`));
  },[accessToken]);

  if(!accessToken)return<div style={{color:C.muted,padding:40,textAlign:"center"}}>Sign in to see intelligence flags on your own entries.</div>;
  if(err)return<div style={{color:C.red,padding:40,textAlign:"center"}}>{err}</div>;
  if(!flags)return<div style={{color:C.muted,padding:40,textAlign:"center"}}>Loading intelligence flags…</div>;

  const total=flags.length;
  const uniqueSubmissions=new Set(flags.map(f=>f.submission_id)).size;
  const byType={};
  flags.forEach(f=>{byType[f.flag_type]=(byType[f.flag_type]||0)+1;});

  const erVisitCount=COMMUNITY_RAW.filter(d=>d.erVisit==="Yes").length;
  const flagTypes=[
    {id:"protocol_anomaly",label:"Protocol Anomaly",color:C.red,desc:"ER visits where Warriors' pain protocols were not followed by clinical staff"},
    {id:"note_type_suggestion",label:"Note Classification",color:C.blue,desc:"Submissions classified by the note-type engine into advocacy, clinical, and support categories"},
    {id:"new_trigger_term",label:"New Trigger Term",color:C.purple,desc:"Submissions containing emergent trigger language not yet in the master list"},
    {id:"cluster_shift",label:"Cluster Shift",color:C.amber,desc:"Submissions suggesting a community-level pattern shift"},
    {id:"frequency_spike",label:"Frequency Spike",color:C.green,desc:"Submissions contributing to an unusual submission frequency event"},
  ];

  const noteTypes={};
  flags.filter(f=>f.flag_type==="note_type_suggestion").forEach(f=>{
    const nt=(f.detail&&f.detail.note_type)||f.suggested_value||"general";
    noteTypes[nt]=(noteTypes[nt]||0)+1;
  });
  const noteTypeSorted=Object.entries(noteTypes).sort((a,b)=>b[1]-a[1]);
  const noteTotalCount=noteTypeSorted.reduce((s,[,c])=>s+c,0);

  const protocolFlags=flags.filter(f=>f.flag_type==="protocol_anomaly");

  return(
    <div className="fade">
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:24}}>
        <Pill label="Total Flags" value={total} accent={C.amber} sub={`${uniqueSubmissions} submissions`} tooltip="Total inference flags generated by the rule-based engine across all historical crisis submissions."/>
        <Pill label="Protocol Anomalies" value={byType.protocol_anomaly||0} accent={C.red} sub={erVisitCount?`${Math.round((byType.protocol_anomaly||0)/erVisitCount*100)}% of ER visits`:"of ER visits"} tooltip="ER visits where the Warrior's established pain protocol was not followed by clinical staff."/>
        <Pill label="Note Classifications" value={byType.note_type_suggestion||0} accent={C.blue} sub={`${noteTypeSorted.length} types`} tooltip="Submissions classified by the note-type engine: advocacy, clinical observation, emotional support, protocol deviation, general."/>
        <Pill label="New Trigger Terms" value={byType.new_trigger_term||0} accent={C.purple} sub="emergent language" tooltip="Submissions containing trigger language not yet in the master trigger list — candidates for community review."/>
        <Pill label="Engine" value="rules-v1" accent={C.teal} sub="Free · No LLM" tooltip="All flags generated by the WIP rule-based inference engine. No AI API calls, no cost per run, fully auditable."/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:22}}>
          <div style={{fontFamily:"Syne",fontWeight:700,marginBottom:3}}>Flag Breakdown</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:18}}>By detection type · rules-v1</div>
          {flagTypes.filter(ft=>(byType[ft.id]||0)>0).map(ft=>{
            const count=byType[ft.id]||0;
            const pct=Math.round(count/total*100);
            return(
              <div key={ft.id} style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:13,color:ft.color,fontWeight:500}}>{ft.label}</span>
                  <span style={{fontSize:12,color:C.muted}}>{count} ({pct}%)</span>
                </div>
                <div style={{fontSize:11,color:C.muted,marginBottom:5,lineHeight:1.4}}>{ft.desc}</div>
                <div style={{height:5,background:"rgba(255,255,255,0.05)",borderRadius:3}}>
                  <div style={{height:"100%",width:`${pct}%`,background:ft.color,borderRadius:3,opacity:0.8}}/>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {noteTypeSorted.length>0&&(
            <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:22,flex:1}}>
              <div style={{fontFamily:"Syne",fontWeight:700,marginBottom:3}}>Note Classification Breakdown</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Submissions by assigned note type</div>
              {noteTypeSorted.map(([nt,count])=>{
                const label=nt.replace(/_/g," ");
                const pct=Math.round(count/noteTotalCount*100);
                return(
                  <div key={nt} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:13,textTransform:"capitalize",color:C.text}}>{label}</span>
                      <span style={{fontSize:12,color:C.muted}}>{count} ({pct}%)</span>
                    </div>
                    <div style={{height:4,background:"rgba(255,255,255,0.05)",borderRadius:3}}>
                      <div style={{height:"100%",width:`${pct}%`,background:C.blue,borderRadius:3,opacity:0.7}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{background:`${C.amber}0a`,border:`1px solid ${C.amber}25`,borderRadius:14,padding:20}}>
            <div style={{fontFamily:"Syne",fontWeight:700,color:C.amber,marginBottom:6,fontSize:14}}>What These Flags Feed</div>
            <div style={{fontSize:13,lineHeight:1.75,color:"rgba(255,255,255,0.7)"}}>Protocol anomalies power ER accountability conversations and hospital quality improvement reports. Note classifications organize crisis narratives for advocacy and grant deliverables. New trigger terms surface language the community is using before it appears in clinical literature.</div>
          </div>
        </div>
      </div>

      {protocolFlags.length>0&&(
        <div style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:14,padding:22}}>
          <div style={{fontFamily:"Syne",fontWeight:700,marginBottom:3}}>Protocol Anomaly Log</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:18}}>{protocolFlags.length} incidents flagged by inference engine</div>
          {protocolFlags.map(f=>{
            const entry=RAW.find(r=>r.id===f.submission_id);
            return(
              <div key={f.id} style={{padding:"12px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:4,marginBottom:4}}>
                  <span style={{fontSize:13,fontWeight:500,color:C.red}}>◈ Protocol Anomaly</span>
                  <span style={{fontSize:11,color:C.muted}}>{f.submission_id}</span>
                </div>
                {entry&&(
                  <div style={{fontSize:12,color:C.muted}}>
                    {entry.hospital&&<span>🏥 {entry.hospital} · </span>}
                    <span>Pain {entry.pain}/10 · {entry.submitted}</span>
                    {entry.notes&&<div style={{marginTop:4,fontStyle:"italic",color:C.amber,borderLeft:`2px solid ${C.amber}40`,paddingLeft:10}}>"{entry.notes.length>140?entry.notes.slice(0,140)+"…":entry.notes}"</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function App(){
  const [tab,setTab]=useState("timeline");
  const [liveData,setLiveData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [postSubmitMode,setPostSubmitMode]=useState(null);
  const [authSession,setAuthSession]=useState(()=>loadSession());
  const [showAuthModal,setShowAuthModal]=useState(false);

  useEffect(()=>{
    // Check for query parameters from Tally redirect
    const params=new URL(window.location.href).searchParams;
    const warriorId=params.get("warrior_id");
    const submissionId=params.get("submission_id")||null;
    if(warriorId){
      setPostSubmitMode({submissionId,warriorId});
      window.history.replaceState({},"",window.location.pathname);
    }

  },[]);

  // Personal timeline: fetched only when signed in, scoped by RLS to the
  // signed-in Warrior's own records. Signed-out visitors see the demo data.
  // Community Patterns / ER Accountability: consent-filtered community view,
  // also signed-in only; on failure COMMUNITY_RAW keeps its demo copy.
  const accessToken = authSession?.accessToken;
  useEffect(()=>{
    if(!accessToken){ setLiveData(null); setLoading(false); return; }
    setLoading(true);
    const load = () => {
      fetchWarriorData(accessToken)
        .then(data=>{ setLiveData(data); setLoading(false); })
        .catch(()=>{ setLiveData(null); setLoading(false); });
      fetchCommunityData(accessToken).then(communityData=>{
        if(!communityData.length) return;
        COMMUNITY_RAW.length=0;
        communityData.forEach(d=>COMMUNITY_RAW.push(d));
      });
    };
    load();
    const poll = setInterval(load, 10 * 60 * 1000); // re-fetch every 10 minutes
    return () => clearInterval(poll);
  },[accessToken]);

  function handleAuthSuccess(session){
    setAuthSession(session);
    setShowAuthModal(false);
    setTab("timeline");
  }
  function handleSignOut(){
    clearSession();
    // Drop cross-Warrior data from memory so a shared device reverts to demo data.
    COMMUNITY_RAW.length = 0;
    RAW_BACKUP.forEach(d => COMMUNITY_RAW.push(d));
    setAuthSession(null);
  }

  const data = liveData && liveData.length > 0 ? liveData : RAW_BACKUP;
  // Inject live data into global RAW reference used by child components.
  // Only when liveData is a distinct array — otherwise data === RAW_BACKUP's
  // contents and RAW already holds them.
  if (liveData && liveData.length > 0) {
    RAW.length = 0;
    data.forEach(d => RAW.push(d));
  } else if (RAW.length !== RAW_BACKUP.length || RAW[0] !== RAW_BACKUP[0]) {
    RAW.length = 0;
    RAW_BACKUP.forEach(d => RAW.push(d));
  }
  const lastUpdated = !loading && data.length ? data.reduce((max,d)=>d.submitted>max?d.submitted:max,"") : null;

  const tabs=[{id:"timeline",label:"My Timeline"},{id:"community",label:"Community Patterns"},{id:"accountability",label:"ER Accountability"},{id:"intelligence",label:"Intelligence"}];
  return(
    <>
      <style>{css}</style>
      <PrintOverlay/>
      {showAuthModal&&<PhoneAuthModal onSuccess={handleAuthSuccess} onClose={()=>setShowAuthModal(false)}/>}
      <div style={{minHeight:"100vh",background:C.bg}}>
        <div style={{borderBottom:`1px solid ${C.border}`,padding:"0 24px",position:"sticky",top:0,background:C.bg,zIndex:10}}>
          <div style={{maxWidth:860,margin:"0 auto"}}>
            <div style={{paddingTop:20,display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
              <div>
                <img src="/Warrior_Intelligence_Project_Logo_v2_Premium_Horizontal_2_no_bg.png" alt="Warrior Intelligence Project" style={{height:38,width:"auto",display:"block",marginBottom:10}}/>
                <div style={{fontSize:10,letterSpacing:3.5,color:C.muted,textTransform:"uppercase",marginBottom:4}}>Human Intelligence Infrastructure</div>
                <div style={{fontFamily:"Syne",fontWeight:800,fontSize:21,letterSpacing:-.3,marginBottom:2}}>Warrior Intelligence <span style={{color:C.amber}}>Dashboard</span></div>
                <div style={{fontSize:12,color:C.muted,marginBottom:14}}>
                  {loading ? "Loading live data..." : !accessToken ? "Demo data · sign in to see your own timeline" : `${data.length} of your crisis reports · Community-owned`}
                  {!loading && accessToken && <span className="live" style={{marginLeft:8,color:C.teal,fontSize:10}}>● LIVE</span>}
                  {lastUpdated && <div style={{fontSize:11,color:C.muted,marginTop:3}}>Last updated: <span style={{color:"rgba(255,255,255,0.55)"}}>{lastUpdated}</span></div>}
                </div>
              </div>
              <div style={{paddingTop:18,flexShrink:0,marginLeft:12}}>
                {authSession
                  ? (<div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:11,color:C.teal,fontWeight:600}}>● Signed In</div>
                        <div style={{fontSize:11,color:C.muted,marginTop:1}}>{authSession.warriorId||"Warrior"}</div>
                      </div>
                      <button onClick={handleSignOut} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 12px",color:C.muted,fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>Sign Out</button>
                    </div>)
                  : (<button onClick={()=>setShowAuthModal(true)} style={{background:C.red,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>🔐 Warrior Login</button>)
                }
              </div>
            </div>
            <div style={{display:"flex",gap:0}}>{tabs.map(t=>(<button key={t.id} onClick={()=>setTab(t.id)} style={{background:"none",border:"none",padding:"10px 20px",fontSize:13,fontWeight:tab===t.id?500:400,color:tab===t.id?C.text:C.muted,borderBottom:tab===t.id?`2px solid ${C.red}`:"2px solid transparent",transition:"all .2s"}}>{t.label}</button>))}</div>
          </div>
        </div>
        <div style={{maxWidth:860,margin:"0 auto",padding:"28px 24px"}}>
          {loading && <div style={{textAlign:"center",paddingTop:80,color:C.muted,fontSize:14}}>Loading Warrior data...</div>}
          {!loading && tab==="timeline"&&<TimelineView postSubmit={postSubmitMode} allData={data} authSession={authSession} onRequestLogin={()=>setShowAuthModal(true)}/>}
          {!loading && !accessToken && (tab==="community"||tab==="accountability") && (
            <div style={{fontSize:12,color:C.amber,background:`${C.amber}12`,border:`1px solid ${C.amber}30`,borderRadius:8,padding:"8px 12px",marginBottom:14}}>
              Demo data shown. Sign in to see live, consent-filtered community patterns.
            </div>
          )}
          {!loading && tab==="community"&&<CommunityView/>}
          {!loading && tab==="accountability"&&<AccountabilityView/>}
          {!loading && tab==="intelligence"&&<InferenceFlagsView authSession={authSession}/>}
        </div>
      </div>
    </>
  );
}
